import React, { useState, useEffect, useRef } from "react";
import {
  Settings,
  BarChart2,
  User,
  HelpCircle,
  X,
  Share2,
  Copy,
  MessageSquare,
  MessageCircle,
  Book,
  ArrowLeft,
  Check,
  Award,
  Calendar,
  Clock,
  Target,
  Flame,
  Lightbulb,
  Volume2,
  VolumeX,
  Eye,
  EyeOff,
  Play,
  Palette,
  Zap,
  Rocket,
  ArrowUp,
  ArrowDown,
  Bug,
  Sparkles,
  RefreshCw,
  FileText,
  Star,
  Crown,
  Medal,
  ThumbsUp,
  CheckCircle,
  Type,
  Sun,
  Moon,
  Sprout,
  Leaf,
  Snowflake,
  Trophy,
  Mail,
  XCircle,
  Coffee,
  Twitter,
  Facebook,
  ChevronRight,
} from "lucide-react";
import puzzles from "./puzzle";
import { supabase } from './supabaseClient';


// Helper functions for stats management
const loadStats = () => {
  const defaultStats = {
    gamesPlayed: 0,
    gamesWon: 0,
    currentStreak: 0,
    maxStreak: 0,
    totalTime: 0,
    averageTime: 0,
    bestTime: null,
    lastPlayedDate: null,
    profile: {
      username: "Player" + Math.floor(Math.random() * 1000),
      level: 1,
      xp: 0,
    },
  };

  try {
    const saved = localStorage.getItem("pivotGameStats");
    return saved ? { ...defaultStats, ...JSON.parse(saved) } : defaultStats;
  } catch {
    return defaultStats;
  }
};

const saveStats = (stats) => {
  localStorage.setItem("pivotGameStats", JSON.stringify(stats));
};

const loadSettings = () => {
  const defaultSettings = {
    theme: "light",
    gameMode: "standard", // "standard", "casual", or "hard"
    highContrast: false,
    soundEnabled: true,
    animationsEnabled: true,
  };

  try {
    const saved = localStorage.getItem("pivotGameSettings");
    return saved
      ? { ...defaultSettings, ...JSON.parse(saved) }
      : defaultSettings;
  } catch {
    return defaultSettings;
  }
};

const saveSettings = (settings) => {
  localStorage.setItem("pivotGameSettings", JSON.stringify(settings));
};

// Theme configurations with enhanced contrast
const themes = {
  light: {
    container: "linear-gradient(135deg, #e0e7ff 0%, #f3e8ff 50%, #fce7f3 100%)",
    gameBg: "rgba(255,255,255,0.8)",
    pivotBg: "linear-gradient(135deg, #6366f1, #a855f7, #ec4899)",
    text: "#000000",
    cardBefore: "linear-gradient(135deg, #dcfce7, #d1fae5)",
    cardAfter: "linear-gradient(135deg, #f3e8ff, #fae8ff)",
    uiBg: "white",
    uiText: "#000000",
    buttonText: "white",
  },
  lightHC: {
    container: "#ffffff",
    gameBg: "#ffffff",
    pivotBg: "#000000",
    text: "#000000",
    cardBefore: "#e8f5e9",
    cardAfter: "#f3e5f5",
    uiBg: "#ffffff",
    uiText: "#000000",
    buttonText: "white",
  },
  dark: {
    container: "linear-gradient(135deg, #1e293b 0%, #334155 50%, #475569 100%)",
    gameBg: "rgba(30, 41, 59, 0.9)",
    pivotBg: "linear-gradient(135deg, #818cf8, #a78bfa, #f472b6)",
    text: "#ffffff",
    cardBefore: "linear-gradient(135deg, #14532d, #166534)",
    cardAfter: "linear-gradient(135deg, #581c87, #6b21a8)",
    uiBg: "#1e293b",
    uiText: "#ffffff",
    buttonText: "white",
  },
  darkHC: {
    container: "#000000",
    gameBg: "#000000",
    pivotBg: "#000000",
    text: "#ffffff",
    cardBefore: "#1b5e20",
    cardAfter: "#4a148c",
    uiBg: "#000000",
    uiText: "#ffffff",
    buttonText: "#ffffff",
  },
  spring: {
    container: "linear-gradient(135deg, #fef3c7 0%, #ecfccb 50%, #bbf7d0 100%)",
    gameBg: "rgba(254, 243, 199, 0.9)",
    pivotBg: "linear-gradient(135deg, #84cc16, #22c55e, #10b981)",
    text: "#3f6212",
    cardBefore: "linear-gradient(135deg, #d9f99d, #bef264)",
    cardAfter: "linear-gradient(135deg, #fde68a, #fcd34d)",
    uiBg: "#ffffff",
    uiText: "#3f6212",
    buttonText: "white",
  },
  springHC: {
    container: "#fffde7",
    gameBg: "#fffde7",
    pivotBg: "#1b5e20",
    text: "#1b5e20",
    cardBefore: "#c5e1a5",
    cardAfter: "#fff59d",
    uiBg: "#ffffff",
    uiText: "#1b5e20",
    buttonText: "#000000",
  },
  summer: {
    container: "linear-gradient(135deg, #fed7aa 0%, #fde68a 50%, #fca5a5 100%)",
    gameBg: "rgba(254, 215, 170, 0.9)",
    pivotBg: "linear-gradient(135deg, #f97316, #eab308, #ef4444)",
    text: "#7c2d12",
    cardBefore: "linear-gradient(135deg, #fed7aa, #fdba74)",
    cardAfter: "linear-gradient(135deg, #fde68a, #facc15)",
    uiBg: "#ffffff",
    uiText: "#7c2d12",
    buttonText: "white",
  },
  summerHC: {
    container: "#fff3e0",
    gameBg: "#fff3e0",
    pivotBg: "#e65100",
    text: "#e65100",
    cardBefore: "#ffcc80",
    cardAfter: "#fff59d",
    uiBg: "#ffffff",
    uiText: "#e65100",
    buttonText: "#000000",
  },
  fall: {
    container: "linear-gradient(135deg, #fee2e2 0%, #ffedd5 50%, #fed7aa 100%)",
    gameBg: "rgba(254, 226, 226, 0.9)",
    pivotBg: "linear-gradient(135deg, #ef4444, #f97316, #eab308)",
    text: "#7c2d12",
    cardBefore: "linear-gradient(135deg, #fecaca, #fca5a5)",
    cardAfter: "linear-gradient(135deg, #ffedd5, #fed7aa)",
    uiBg: "#ffffff",
    uiText: "#7c2d12",
    buttonText: "white",
  },
  fallHC: {
    container: "#fbe9e7",
    gameBg: "#fbe9e7",
    pivotBg: "#bf360c",
    text: "#bf360c",
    cardBefore: "#ffccbc",
    cardAfter: "#ffe0b2",
    uiBg: "#ffffff",
    uiText: "#bf360c",
    buttonText: "#000000",
  },
  winter: {
    container: "linear-gradient(135deg, #e0f2fe 0%, #f0f9ff 50%, #f8fafc 100%)",
    gameBg: "rgba(224, 242, 254, 0.9)",
    pivotBg: "linear-gradient(135deg, #0284c7, #0ea5e9, #38bdf8)",
    text: "#0c4a6e",
    cardBefore: "linear-gradient(135deg, #bae6fd, #7dd3fc)",
    cardAfter: "linear-gradient(135deg, #e0f2fe, #bae6fd)",
    uiBg: "#ffffff",
    uiText: "#0c4a6e",
    buttonText: "white",
  },
  winterHC: {
    container: "#e1f5fe",
    gameBg: "#e1f5fe",
    pivotBg: "#01579b",
    text: "#01579b",
    cardBefore: "#81d4fa",
    cardAfter: "#b3e5fc",
    uiBg: "#ffffff",
    uiText: "#01579b",
    buttonText: "#000000",
  },
};

const PUZZLE_START_DATE = new Date(2025, 0, 1);

const TutorialCarousel = ({ theme, settings, getTitleColor, onComplete }) => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    {
      icon: Target,
      iconColor: "#6366f1",
      iconBg: "#e0e7ff",
      title: "Find Words",
      description:
        "Find words that make valid phrases before OR after the pivot word.",
      visual: (
        <div style={{ textAlign: "center", marginTop: "20px" }}>
          <div
            style={{
              display: "inline-block",
              padding: "16px 32px",
              background: "linear-gradient(135deg, #6366f1, #a855f7)",
              color: "white",
              borderRadius: "16px",
              fontSize: "28px",
              fontWeight: "900",
            }}
          >
            BREAK
          </div>
          <div
            style={{ marginTop: "12px", fontSize: "14px", color: "#6b7280" }}
          >
            Example pivot word
          </div>
        </div>
      ),
    },
    {
      icon: ArrowUp,
      iconColor: "#16a34a",
      iconBg: "#dcfce7",
      title: "Before Words",
      description:
        "Type a word that comes BEFORE the pivot. Use the green arrow to submit.",
      visual: (
        <div style={{ marginTop: "20px" }}>
          <div
            style={{
              background: "#dcfce7",
              borderRadius: "12px",
              padding: "16px",
              border: "2px solid #22c55e",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
              }}
            >
              <span
                style={{
                  fontSize: "22px",
                  fontWeight: "bold",
                  color: "#16a34a",
                }}
              >
                lunch
              </span>
              <span style={{ color: "#9ca3af", fontSize: "18px" }}>+</span>
              <span
                style={{
                  fontSize: "22px",
                  fontWeight: "bold",
                  color: "#6366f1",
                }}
              >
                break
              </span>
            </div>
            <div
              style={{
                textAlign: "center",
                marginTop: "8px",
                fontSize: "14px",
                color: "#16a34a",
              }}
            >
              ✓ "lunch break" is valid!
            </div>
          </div>
        </div>
      ),
    },
    {
      icon: ArrowDown,
      iconColor: "#9333ea",
      iconBg: "#f3e8ff",
      title: "After Words",
      description:
        "Type a word that comes AFTER the pivot. Use the purple arrow to submit.",
      visual: (
        <div style={{ marginTop: "20px" }}>
          <div
            style={{
              background: "#f3e8ff",
              borderRadius: "12px",
              padding: "16px",
              border: "2px solid #9333ea",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
              }}
            >
              <span
                style={{
                  fontSize: "22px",
                  fontWeight: "bold",
                  color: "#6366f1",
                }}
              >
                break
              </span>
              <span style={{ color: "#9ca3af", fontSize: "18px" }}>+</span>
              <span
                style={{
                  fontSize: "22px",
                  fontWeight: "bold",
                  color: "#9333ea",
                }}
              >
                dance
              </span>
            </div>
            <div
              style={{
                textAlign: "center",
                marginTop: "8px",
                fontSize: "14px",
                color: "#9333ea",
              }}
            >
              ✓ "break dance" is valid!
            </div>
          </div>
        </div>
      ),
    },
    {
      icon: Lightbulb,
      iconColor: "#ca8a04",
      iconBg: "#fef3c7",
      title: "Use Hints",
      description:
        "Stuck? Use up to 3 hints per puzzle to help find words.",
      visual: (
        <div style={{ marginTop: "20px" }}>
          <div
            style={{
              background: "#fef3c7",
              borderRadius: "12px",
              padding: "16px",
              border: "2px solid #fbbf24",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "8px",
              }}
            >
              <Lightbulb size={20} color="#ca8a04" />
              <span style={{ fontWeight: "600", color: "#92400e" }}>
                Hint 1/3
              </span>
            </div>
            <p style={{ color: "#92400e", margin: 0, fontSize: "15px" }}>
              "Think about meals and dance styles..."
            </p>
          </div>
        </div>
      ),
    },
    {
      icon: Calendar,
      iconColor: "#0284c7",
      iconBg: "#e0f2fe",
      title: "Daily Challenge",
      description: "A new puzzle every day at midnight. Build your streak!",
      visual: (
        <div style={{ marginTop: "20px", textAlign: "center" }}>
          <div
            style={{
              display: "inline-flex",
              gap: "8px",
              background: "linear-gradient(135deg, #fbbf24, #f59e0b)",
              padding: "12px 24px",
              borderRadius: "9999px",
              color: "white",
              fontWeight: "bold",
              fontSize: "18px",
              alignItems: "center",
            }}
          >
            <Flame size={20} /> 7 Day Streak!
          </div>
          <p
            style={{ marginTop: "12px", fontSize: "14px", color: "#6b7280" }}
          >
            Come back daily to keep it alive
          </p>
        </div>
      ),
    },
  ];

  const nextSlide = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const slide = slides[currentSlide];
  const isLastSlide = currentSlide === slides.length - 1;

  return (
    <div
      style={{
        background: theme.gameBg,
        borderRadius: "24px",
        padding: "32px 24px",
        maxWidth: "420px",
        width: "100%",
        boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
        position: "relative",
      }}
    >
      {/* Progress Dots */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: "8px",
          marginBottom: "24px",
        }}
      >
        {slides.map((_, idx) => (
          <div
            key={idx}
            onClick={() => setCurrentSlide(idx)}
            style={{
              width: idx === currentSlide ? "24px" : "8px",
              height: "8px",
              borderRadius: "4px",
              background: idx === currentSlide ? getTitleColor() : theme.text + "30",
              cursor: "pointer",
              transition: "all 0.3s",
            }}
          />
        ))}
      </div>

      {/* Slide Content */}
      <div style={{ minHeight: "320px" }}>
        {/* Icon */}
        <div
          style={{
            width: "80px",
            height: "80px",
            borderRadius: "20px",
            background: slide.iconBg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 20px",
            color: slide.iconColor,
          }}
        >
          <slide.icon size={40} />
        </div>

        {/* Title */}
        <h2
          style={{
            fontSize: "28px",
            fontWeight: "900",
            color: theme.text,
            textAlign: "center",
            marginBottom: "12px",
          }}
        >
          {slide.title}
        </h2>

        {/* Description */}
        <p
          style={{
            fontSize: "16px",
            color: theme.text + "99",
            textAlign: "center",
            lineHeight: "1.5",
            margin: "0 auto",
            maxWidth: "300px",
          }}
        >
          {slide.description}
        </p>

        {/* Visual */}
        {slide.visual}
      </div>

      {/* Navigation */}
      <div style={{ display: "flex", gap: "12px", marginTop: "24px" }}>
        {currentSlide > 0 && (
          <button
            onClick={prevSlide}
            style={{
              padding: "16px 20px",
              background: theme.uiBg,
              color: theme.uiText,
              fontWeight: "600",
              fontSize: "16px",
              borderRadius: "12px",
              border: `1px solid ${theme.text}20`,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <ArrowLeft size={20} /> Back
          </button>
        )}

        <button
          onClick={isLastSlide ? onComplete : nextSlide}
          style={{
            flex: 1,
            padding: "16px 24px",
            background: isLastSlide
              ? "linear-gradient(135deg, #22c55e, #16a34a)"
              : theme.pivotBg,
            color: "white",
            fontWeight: "bold",
            fontSize: "16px",
            borderRadius: "12px",
            border: "none",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
          }}
        >
          {isLastSlide ? (
            <>
              Let's Play! <Rocket size={20} />
            </>
          ) : (
            <>
              Next <ChevronRight size={20} />
            </>
          )}
        </button>
      </div>

      {/* Skip Button */}
      {!isLastSlide && (
        <button
          onClick={onComplete}
          style={{
            width: "100%",
            padding: "12px",
            background: "none",
            color: theme.text + "80",
            border: "none",
            cursor: "pointer",
            marginTop: "12px",
            fontSize: "14px",
          }}
        >
          Skip tutorial
        </button>
      )}
    </div>
  );
};

// Settings Tabs Component
const SettingsTabs = ({
  theme,
  settings,
  setSettings,
  saveSettings,
  showKeyboard,
  setShowKeyboard,
  getTitleColor,
  onClose,
  playSound,
}) => {
  const [activeTab, setActiveTab] = useState("gameplay");

  const tabs = [
    { id: "gameplay", label: "Gameplay" },
    { id: "appearance", label: "Theme" },
    { id: "sound", label: "Sound" },
  ];

  // Helper to get readable text color based on background
  const getTextColor = (isActive, activeColor) => {
    if (!isActive) return theme.text;
    return activeColor;
  };

  // Toggle component with proper contrast
  const SettingsToggle = ({ enabled, onToggle, color = "#4ade80" }) => (
    <div
      onClick={onToggle}
      style={{
        position: "relative",
        width: "50px",
        height: "26px",
        background: enabled ? color : theme.text + "40",
        borderRadius: "13px",
        cursor: "pointer",
        transition: "background 0.3s",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: "3px",
          left: enabled ? "27px" : "3px",
          width: "20px",
          height: "20px",
          background: "white",
          borderRadius: "50%",
          transition: "left 0.3s",
          boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
        }}
      />
    </div>
  );

  return (
    <div
      style={{
        background: theme.gameBg,
        borderRadius: "24px",
        padding: "24px",
        maxWidth: "420px",
        width: "100%",
        boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: "20px",
        }}
      >
        <button
          onClick={onClose}
          style={{
            background: "none",
            border: "none",
            color: theme.text,
            cursor: "pointer",
            padding: "8px",
            display: "flex",
            alignItems: "center",
            gap: "4px",
            fontSize: "16px",
          }}
        >
          <ArrowLeft size={20} />
        </button>
        <h2
          style={{
            flex: 1,
            fontSize: "24px",
            fontWeight: "900",
            color: getTitleColor(),
            textAlign: "center",
            marginRight: "40px",
          }}
        >
          Settings
        </h2>
      </div>

      {/* Tabs */}
      <div
        style={{
          display: "flex",
          gap: "4px",
          background: theme.text + "15",
          padding: "4px",
          borderRadius: "12px",
          marginBottom: "24px",
        }}
      >
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              flex: 1,
              padding: "12px 8px",
              borderRadius: "10px",
              border: "none",
              background: activeTab === tab.id ? theme.uiBg : "transparent",
              color: activeTab === tab.id ? getTitleColor() : theme.text + "80",
              fontWeight: "600",
              fontSize: "14px",
              cursor: "pointer",
              transition: "all 0.2s",
              boxShadow:
                activeTab === tab.id ? "0 2px 8px rgba(0,0,0,0.1)" : "none",
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div style={{ minHeight: "320px" }}>
        {/* Gameplay Tab */}
        {activeTab === "gameplay" && (
          <div>
            <p
              style={{
                fontSize: "12px",
                color: theme.text + "80",
                marginBottom: "16px",
              }}
            >
              Choose your play style
            </p>

            {/* Game Modes */}
            {[
              {
                id: "standard",
                icon: Target,
                label: "Standard",
                desc: "Timer + 5 guesses + hints",
                color: "#6366f1",
                activeBg: "#e0e7ff",
                activeText: "#4338ca",
              },
              {
                id: "casual",
                icon: Zap,
                label: "Casual",
                desc: "No pressure, unlimited guesses",
                color: "#22c55e",
                activeBg: "#dcfce7",
                activeText: "#166534",
              },
              {
                id: "hard",
                icon: Flame,
                label: "Hard",
                desc: "No hints, hidden word lists",
                color: "#ef4444",
                activeBg: "#fee2e2",
                activeText: "#b91c1c",
              },
            ].map((mode) => {
              const isActive = settings.gameMode === mode.id;
              return (
                <div
                  key={mode.id}
                  onClick={() => {
                    const newSettings = { ...settings, gameMode: mode.id };
                    setSettings(newSettings);
                    saveSettings(newSettings);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "14px",
                    background: isActive ? mode.activeBg : theme.uiBg,
                    border: `2px solid ${isActive ? mode.color : "transparent"}`,
                    borderRadius: "12px",
                    marginBottom: "8px",
                    cursor: "pointer",
                    transition: "all 0.2s",
                  }}
                >
                  <div
                    style={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "10px",
                      background: isActive ? mode.color : theme.text + "20",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: isActive ? "white" : theme.text + "80",
                    }}
                  >
                    <mode.icon size={20} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        fontWeight: "600",
                        color: isActive ? mode.activeText : theme.text,
                      }}
                    >
                      {mode.label}
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: isActive ? mode.activeText + "cc" : theme.text + "80",
                      }}
                    >
                      {mode.desc}
                    </div>
                  </div>
                  <div
                    style={{
                      width: "20px",
                      height: "20px",
                      borderRadius: "50%",
                      border: `2px solid ${isActive ? mode.color : theme.text + "40"}`,
                      background: isActive ? mode.color : "transparent",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {isActive && (
                      <div
                        style={{
                          width: "8px",
                          height: "8px",
                          borderRadius: "50%",
                          background: "white",
                        }}
                      />
                    )}
                  </div>
                </div>
              );
            })}

            {/* Keyboard Toggle */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "14px",
                background: theme.uiBg,
                borderRadius: "12px",
                marginTop: "16px",
                border: `1px solid ${theme.text}15`,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "10px",
                    background: showKeyboard ? "#e0e7ff" : theme.text + "15",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: showKeyboard ? "#6366f1" : theme.text + "80",
                  }}
                >
                  <Type size={20} />
                </div>
                <div>
                  <div style={{ fontWeight: "600", color: theme.text }}>
                    Onscreen Keyboard
                  </div>
                  <div style={{ fontSize: "12px", color: theme.text + "80" }}>
                    Virtual keyboard for mobile
                  </div>
                </div>
              </div>
              <SettingsToggle
                enabled={showKeyboard}
                onToggle={() => setShowKeyboard(!showKeyboard)}
                color="#6366f1"
              />
            </div>
          </div>
        )}

        {/* Appearance Tab */}
        {activeTab === "appearance" && (
          <div>
            <p
              style={{
                fontSize: "12px",
                color: theme.text + "80",
                marginBottom: "16px",
              }}
            >
              Choose your theme
            </p>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "12px",
                marginBottom: "16px",
              }}
            >
              {[
                {
                  id: "light",
                  icon: Sun,
                  label: "Light",
                  bg: "linear-gradient(135deg, #f3e8ff, #e0e7ff)",
                  textColor: "#4f46e5",
                },
                {
                  id: "dark",
                  icon: Moon,
                  label: "Dark",
                  bg: "linear-gradient(135deg, #1e293b, #334155)",
                  textColor: "#ffffff",
                },
                {
                  id: "spring",
                  icon: Sprout,
                  label: "Spring",
                  bg: "linear-gradient(135deg, #ecfccb, #bbf7d0)",
                  textColor: "#166534",
                },
                {
                  id: "summer",
                  icon: Sun,
                  label: "Summer",
                  bg: "linear-gradient(135deg, #fed7aa, #fde68a)",
                  textColor: "#c2410c",
                },
                {
                  id: "fall",
                  icon: Leaf,
                  label: "Fall",
                  bg: "linear-gradient(135deg, #fee2e2, #ffedd5)",
                  textColor: "#dc2626",
                },
                {
                  id: "winter",
                  icon: Snowflake,
                  label: "Winter",
                  bg: "linear-gradient(135deg, #e0f2fe, #f0f9ff)",
                  textColor: "#0284c7",
                },
              ].map((t) => {
                const isActive = settings.theme === t.id;
                return (
                  <div
                    key={t.id}
                    onClick={() => {
                      const newSettings = { ...settings, theme: t.id };
                      setSettings(newSettings);
                      saveSettings(newSettings);
                    }}
                    style={{
                      padding: "16px",
                      background: t.bg,
                      borderRadius: "16px",
                      border: `3px solid ${isActive ? t.textColor : "transparent"}`,
                      cursor: "pointer",
                      textAlign: "center",
                      transition: "all 0.2s",
                      boxShadow: isActive ? "0 4px 12px rgba(0,0,0,0.15)" : "none",
                    }}
                  >
                    <div
                      style={{
                        width: "44px",
                        height: "44px",
                        borderRadius: "50%",
                        background: t.id === "dark" ? "#475569" : "rgba(255,255,255,0.8)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        margin: "0 auto 8px",
                        color: t.textColor,
                      }}
                    >
                      <t.icon size={22} />
                    </div>
                    <div
                      style={{
                        fontWeight: "600",
                        fontSize: "13px",
                        color: t.textColor,
                      }}
                    >
                      {t.label}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* High Contrast Toggle */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "14px",
                background: settings.highContrast ? "#fef3c7" : theme.uiBg,
                borderRadius: "12px",
                border: settings.highContrast ? "2px solid #f59e0b" : `1px solid ${theme.text}15`,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "10px",
                    background: settings.highContrast ? "#f59e0b" : theme.text + "15",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: settings.highContrast ? "white" : theme.text + "80",
                  }}
                >
                  <Eye size={20} />
                </div>
                <div>
                  <div
                    style={{
                      fontWeight: "600",
                      color: settings.highContrast ? "#92400e" : theme.text,
                    }}
                  >
                    High Contrast
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: settings.highContrast ? "#b45309" : theme.text + "80",
                    }}
                  >
                    Better visibility
                  </div>
                </div>
              </div>
              <SettingsToggle
                enabled={settings.highContrast}
                onToggle={() => {
                  const newSettings = {
                    ...settings,
                    highContrast: !settings.highContrast,
                  };
                  setSettings(newSettings);
                  saveSettings(newSettings);
                }}
                color="#f59e0b"
              />
            </div>
          </div>
        )}

        {/* Sound Tab */}
        {activeTab === "sound" && (
          <div>
            <p
              style={{
                fontSize: "12px",
                color: theme.text + "80",
                marginBottom: "16px",
              }}
            >
              Audio & visual effects
            </p>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "16px",
                background: theme.uiBg,
                borderRadius: "12px",
                marginBottom: "12px",
                border: `1px solid ${theme.text}15`,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <div
                  style={{
                    width: "44px",
                    height: "44px",
                    borderRadius: "12px",
                    background: settings.soundEnabled ? "#dcfce7" : theme.text + "15",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: settings.soundEnabled ? "#16a34a" : theme.text + "80",
                  }}
                >
                  {settings.soundEnabled ? <Volume2 size={22} /> : <VolumeX size={22} />}
                </div>
                <div>
                  <div style={{ fontWeight: "600", color: theme.text }}>
                    Sound Effects
                  </div>
                  <div style={{ fontSize: "12px", color: theme.text + "80" }}>
                    Beeps and victory sounds
                  </div>
                </div>
              </div>
              <SettingsToggle
                enabled={settings.soundEnabled}
                onToggle={() => {
                  const newSettings = {
                    ...settings,
                    soundEnabled: !settings.soundEnabled,
                  };
                  setSettings(newSettings);
                  saveSettings(newSettings);
                }}
              />
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "16px",
                background: theme.uiBg,
                borderRadius: "12px",
                border: `1px solid ${theme.text}15`,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <div
                  style={{
                    width: "44px",
                    height: "44px",
                    borderRadius: "12px",
                    background: settings.animationsEnabled ? "#e0e7ff" : theme.text + "15",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: settings.animationsEnabled ? "#6366f1" : theme.text + "80",
                  }}
                >
                  <Sparkles size={22} />
                </div>
                <div>
                  <div style={{ fontWeight: "600", color: theme.text }}>
                    Animations
                  </div>
                  <div style={{ fontSize: "12px", color: theme.text + "80" }}>
                    Celebrations and transitions
                  </div>
                </div>
              </div>
              <SettingsToggle
                enabled={settings.animationsEnabled}
                onToggle={() => {
                  const newSettings = {
                    ...settings,
                    animationsEnabled: !settings.animationsEnabled,
                  };
                  setSettings(newSettings);
                  saveSettings(newSettings);
                }}
                color="#6366f1"
              />
            </div>

            {/* Sound Test Button */}
            <button
              onClick={() => playSound("success")}
              style={{
                width: "100%",
                marginTop: "24px",
                padding: "14px",
                background: settings.soundEnabled ? theme.pivotBg : theme.text + "20",
                color: settings.soundEnabled ? "white" : theme.text + "60",
                fontWeight: "600",
                fontSize: "15px",
                borderRadius: "12px",
                border: "none",
                cursor: settings.soundEnabled ? "pointer" : "not-allowed",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
              }}
              disabled={!settings.soundEnabled}
            >
              <Volume2 size={18} /> Test Sound
            </button>
          </div>
        )}
      </div>

      {/* Done Button */}
      <button
        onClick={onClose}
        style={{
          width: "100%",
          padding: "16px",
          background: theme.pivotBg,
          color: "white",
          fontWeight: "bold",
          fontSize: "16px",
          borderRadius: "12px",
          border: "none",
          cursor: "pointer",
          marginTop: "20px",
        }}
      >
        Done
      </button>
    </div>
  );
};
const PivotGame = () => {
  // Comprehensive puzzle database

  const getDailyPuzzle = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startDate = new Date(PUZZLE_START_DATE);
    startDate.setHours(0, 0, 0, 0);

    const timeDiff = today - startDate;
    const daysSinceStart = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
    const puzzleNumber = daysSinceStart + 1;
    const puzzleIndex = daysSinceStart % puzzles.length;
    const dailyPuzzle = puzzles[puzzleIndex];

    return {
      ...dailyPuzzle,
      puzzleNumber: puzzleNumber,
    };
  };
  
  const TARGET_WORDS = 10;
  const [gameData] = useState(getDailyPuzzle());
  const [stats, setStats] = useState(loadStats());
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [settings, setSettings] = useState(loadSettings());
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [beforeInput, setBeforeInput] = useState("");
  const [afterInput, setAfterInput] = useState("");
  const [foundBefore, setFoundBefore] = useState([]);
  const [foundAfter, setFoundAfter] = useState([]);
  const [message, setMessage] = useState(null);
  const [shake, setShake] = useState(false);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentHintIndex, setCurrentHintIndex] = useState(-1);
  const [celebrationParticles, setCelebrationParticles] = useState([]);
  const [showTutorial, setShowTutorial] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showCopyright, setShowCopyright] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [helpSection, setHelpSection] = useState("menu");

  // Welcome & Auth states
  const [showWelcome, setShowWelcome] = useState(true);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [isNewUser, setIsNewUser] = useState(false);

  // Auth State
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [authMode, setAuthMode] = useState('login'); // 'login' or 'signup'
  const [emailInput, setEmailInput] = useState('');
  const [passwordInput, setPasswordInput] = useState('');

  // Suggestion modal
  const [showSuggestModal, setShowSuggestModal] = useState(false);
  const [suggestionWord, setSuggestionWord] = useState("");
  const [suggestionType, setSuggestionType] = useState("before");
  const [suggestionSubmitted, setSuggestionSubmitted] = useState(false);

  // Victory modal
  const [showVictoryModal, setShowVictoryModal] = useState(false);

  // Wrong guess tracking (Standard mode only)
  const [wrongGuesses, setWrongGuesses] = useState(0);
  const MAX_WRONG_GUESSES = 5;
  const [hasWonPuzzle, setHasWonPuzzle] = useState(false);


  
  // Game over state
  const [isGameOver, setIsGameOver] = useState(false);
  const [showGameOverModal, setShowGameOverModal] = useState(false);

  // Onscreen keyboard
  const [activeInput, setActiveInput] = useState("before"); // "before" or "after"
  const [showKeyboard, setShowKeyboard] = useState(false);

  const audioContextRef = useRef(null);
  const totalWords =
    new Set(gameData.validBefore).size + new Set(gameData.validAfter).size;
  const foundWords = foundBefore.length + foundAfter.length;
  const baseWordsFound = Math.min(foundWords, TARGET_WORDS);
  const bonusWords = Math.max(0, foundWords - TARGET_WORDS);

  // Initialize AudioContext once
  useEffect(() => {
    if (typeof window !== "undefined") {
      audioContextRef.current = new (window.AudioContext ||
        window.webkitAudioContext)();
    }
  }, []);

  // Detect new user
  useEffect(() => {
    setIsNewUser(stats.gamesPlayed === 0);
  }, [stats.gamesPlayed]);

  // Hybrid validation API call
const validateWithAPI = async (word, pivotWord, position) => {
  try {
    const response = await fetch(
      'https://zqlcpwmvoflnkbowkksh.supabase.co/functions/v1/validate-word',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InpxbGNwd212b2Zsbmtib3dra3NoIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjY4NTQ1OTUsImV4cCI6MjA4MjQzMDU5NX0.m4SmO2v9pmJD63qjRzSkdCFOxRSbi0IR4QAVjaTNdoQ',
        },
        body: JSON.stringify({
          guessedWord: word,
          pivotWord: pivotWord,
          position: position
        })
      }
    );

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Validation API error:', error);
    return { valid: false, source: 'error', reason: 'Network error' };
  }
};

  const playSound = (type) => {
    if (!settings.soundEnabled || !audioContextRef.current) return;

    const oscillator = audioContextRef.current.createOscillator();
    const gainNode = audioContextRef.current.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContextRef.current.destination);

    switch (type) {
      case "success":
        oscillator.frequency.value = 800;
        gainNode.gain.setValueAtTime(0.3, audioContextRef.current.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(
          0.01,
          audioContextRef.current.currentTime + 0.2
        );
        oscillator.start(audioContextRef.current.currentTime);
        oscillator.stop(audioContextRef.current.currentTime + 0.2);
        break;
      case "error":
        oscillator.frequency.value = 200;
        gainNode.gain.setValueAtTime(0.2, audioContextRef.current.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(
          0.01,
          audioContextRef.current.currentTime + 0.15
        );
        oscillator.start(audioContextRef.current.currentTime);
        oscillator.stop(audioContextRef.current.currentTime + 0.15);
        break;
      case "victory":
        [523, 587, 659, 784].forEach((freq, i) => {
          const osc = audioContextRef.current.createOscillator();
          const gain = audioContextRef.current.createGain();
          osc.connect(gain);
          gain.connect(audioContextRef.current.destination);
          osc.frequency.value = freq;
          gain.gain.setValueAtTime(
            0.3,
            audioContextRef.current.currentTime + i * 0.15
          );
          gain.gain.exponentialRampToValueAtTime(
            0.01,
            audioContextRef.current.currentTime + i * 0.15 + 0.2
          );
          osc.start(audioContextRef.current.currentTime + i * 0.15);
          osc.stop(audioContextRef.current.currentTime + i * 0.15 + 0.2);
        });
        break;
      case "gameOver":
        // Descending tones for game over
        [400, 300, 200].forEach((freq, i) => {
          const osc = audioContextRef.current.createOscillator();
          const gain = audioContextRef.current.createGain();
          osc.connect(gain);
          gain.connect(audioContextRef.current.destination);
          osc.frequency.value = freq;
          osc.type = "triangle";
          gain.gain.setValueAtTime(
            0.2,
            audioContextRef.current.currentTime + i * 0.2
          );
          gain.gain.exponentialRampToValueAtTime(
            0.01,
            audioContextRef.current.currentTime + i * 0.2 + 0.3
          );
          osc.start(audioContextRef.current.currentTime + i * 0.2);
          osc.stop(audioContextRef.current.currentTime + i * 0.2 + 0.3);
        });
        break;
      case "warning":
        // Warning beep when low on guesses
        const osc = audioContextRef.current.createOscillator();
        const gain = audioContextRef.current.createGain();
        osc.connect(gain);
        gain.connect(audioContextRef.current.destination);
        osc.frequency.value = 440;
        osc.type = "square";
        gain.gain.setValueAtTime(0.15, audioContextRef.current.currentTime);
        gain.gain.exponentialRampToValueAtTime(
          0.01,
          audioContextRef.current.currentTime + 0.1
        );
        osc.start(audioContextRef.current.currentTime);
        osc.stop(audioContextRef.current.currentTime + 0.1);
        break;
      default:
        break;
    }
  };

  // Validate word using Free Dictionary API
  const isValidEnglishWord = async (word) => {
    try {
      const response = await fetch(
        `https://api.dictionaryapi.dev/api/v2/entries/en/${word.toLowerCase()}`
      );
      return response.ok;
    } catch {
      // If API fails, accept the word (fail gracefully)
      return true;
    }
  };

  // Timer only runs in Standard mode
  useEffect(() => {
    let interval;
    if (isPlaying && !hasWonPuzzle && !isGameOver && settings.gameMode !== "casual") {
      interval = setInterval(() => {
        setTimeElapsed((prev) => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isPlaying, hasWonPuzzle, isGameOver, settings.gameMode]);

  useEffect(() => {
    if (foundWords >= TARGET_WORDS && isPlaying && !hasWonPuzzle) {
      setHasWonPuzzle(true);
      setShowVictoryModal(true);
  
      try {
        const today = new Date().toDateString();
        const newStats = {
          ...stats,
          gamesPlayed: stats.gamesPlayed + 1,
          gamesWon: stats.gamesWon + 1,
          totalTime: stats.totalTime + timeElapsed,
          currentStreak:
            stats.lastPlayedDate === today
              ? stats.currentStreak
              : stats.lastPlayedDate ===
                new Date(Date.now() - 86400000).toDateString()
                ? stats.currentStreak + 1
                : 1,
          maxStreak: Math.max(
            stats.maxStreak,
            stats.currentStreak ? stats.currentStreak + 1 : 1
          ),
          lastPlayedDate: today,
          bestTime: stats.bestTime
            ? Math.min(stats.bestTime, timeElapsed)
            : timeElapsed,
          averageTime: (stats.totalTime + timeElapsed) / (stats.gamesWon + 1),
        };
  
        setStats(newStats);
        try { saveStats(newStats); } catch (e) { console.error("Failed to save stats:", e); }
  
        if (user) {
          saveCloudStats(newStats);
          saveGameSession(true);
        }
  
        try {
          triggerCelebration();
          playSound("victory");
        } catch (error) {
          console.error("Celebration error:", error);
        }
      } catch (err) {
        console.error("Error updating victory state:", err);
      }
    }
  }, [foundWords, isPlaying, hasWonPuzzle, stats, timeElapsed]);

  // Game Over detection (Standard mode only)
  useEffect(() => {
    if (wrongGuesses >= MAX_WRONG_GUESSES && settings.gameMode !== "casual" && !isGameOver && !hasWonPuzzle) {
      setIsGameOver(true);
      setIsPlaying(false);
      setShowGameOverModal(true);
      playSound("gameOver");

      const today = new Date().toDateString();
      const newStats = {
        ...stats,
        gamesPlayed: stats.gamesPlayed + 1,
        currentStreak: 0,
        lastPlayedDate: today,
      };
      setStats(newStats);
      saveStats(newStats);

      if (user) {
        saveCloudStats(newStats);
        saveGameSession(false);
      }
    }
  }, [wrongGuesses, settings.gameMode, isGameOver]);

  // Handle Enter key for tutorial modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Enter" && showTutorial) {
        e.preventDefault();
        startGame();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [showTutorial]);

  // Prevent body scroll when modals are open
  useEffect(() => {
    const isModalOpen = showTutorial || showVictoryModal || showGameOverModal || showWelcome || showSettingsModal || showStatsModal || showProfileModal || showHelp || showTerms || showCopyright || showSuggestModal;
    if (isModalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [showTutorial, showVictoryModal, showGameOverModal, showWelcome, showSettingsModal, showStatsModal, showProfileModal, showHelp, showTerms, showCopyright, showSuggestModal]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Haptic feedback for mobile (safe for all devices)
  const triggerHaptic = (type = "light") => {
    // Only run if vibrate API is supported (mobile devices)
    if (typeof navigator !== "undefined" && navigator.vibrate) {
      try {
        switch (type) {
          case "light":
            navigator.vibrate(10);
            break;
          case "medium":
            navigator.vibrate(25);
            break;
          case "heavy":
            navigator.vibrate([50, 30, 50]);
            break;
          case "error":
            navigator.vibrate([100, 50, 100]);
            break;
          case "success":
            navigator.vibrate([10, 50, 10, 50, 10]);
            break;
          default:
            navigator.vibrate(10);
        }
      } catch (e) {
        // Silently fail if vibration not supported
        console.log("Haptic feedback not supported");
      }
    }
    // Does nothing on desktop/laptop - no error thrown
  };

  const checkWord = async (input, type) => {
    const word = input.toLowerCase().trim();
    if (!word) return;

    if (type === "before") {
      // Check if already found
      if (foundBefore.includes(word)) {
        setMessage({
          text: (
            <>
              <RefreshCw size={20} /> Already found!
            </>
          ),
          type: "error",
        });
        triggerShake();
        playSound("error");
        return;
      }

      // Step 1: Check hardcoded list first (instant, free)
      if (gameData.validBefore.includes(word)) {
        const newFound = [...foundBefore, word];
        setFoundBefore(newFound);
        setMessage({
          text: (
            <>
              <Sparkles size={20} /> {hasWonPuzzle ? `Bonus! +${bonusWords + 1}` : "Perfect!"}
            </>
          ),
          type: hasWonPuzzle ? "bonus" : "success",
        });
        setBeforeInput("");
        triggerSuccess();
        playSound("success");
        triggerHaptic("success");
        return;
      }

      // Step 2: Not in hardcoded list - try API validation
      setMessage({
        text: (
          <>
            <RefreshCw size={20} className="spin" /> Checking...
          </>
        ),
        type: "info",
      });

      const apiResult = await validateWithAPI(word, gameData.pivotWord, "before");
      console.log("API Result (before):", apiResult);
    
      if (apiResult.valid) {
        // AI approved this word!
        const newFound = [...foundBefore, word];
        setFoundBefore(newFound);
        setMessage({
          text: (
            <>
              <Sparkles size={20} /> {hasWonPuzzle ? `Bonus! +${bonusWords + 1}` : "Perfect!"}
            </>
          ),
          type: hasWonPuzzle ? "bonus" : "success",
        });
        setBeforeInput("");
        triggerSuccess();
        playSound("success");
        triggerHaptic("success");
      } else {
        // Wrong guess - only count in Standard/Hard mode
        if (settings.gameMode !== "casual") {
          setWrongGuesses((prev) => prev + 1);
        }
        setMessage({
          text: (
            <>
              <X size={20} /> Not a valid phrase
              {settings.gameMode !== "casual" && wrongGuesses < MAX_WRONG_GUESSES - 1 && (
                <span style={{ marginLeft: "8px", fontSize: "14px" }}>
                  ({MAX_WRONG_GUESSES - wrongGuesses - 1} left)
                </span>
              )}
            </>
          ),
          type: "error",
        });
        triggerShake();
        playSound("error");
        triggerHaptic("error");
      }
    } else {
      // === AFTER WORDS ===
      // Check if already found
      if (foundAfter.includes(word)) {
        setMessage({
          text: (
            <>
              <RefreshCw size={20} /> Already found!
            </>
          ),
          type: "error",
        });
        triggerShake();
        playSound("error");
        return;
      }

      // Step 1: Check hardcoded list first (instant, free)
      if (gameData.validAfter.includes(word)) {
        const newFound = [...foundAfter, word];
        setFoundAfter(newFound);
        setMessage({
          text: (
            <>
              <Sparkles size={20} /> {hasWonPuzzle 
                ? `Bonus! +${bonusWords + 1}` 
                : (apiResult.source === "ai_validated" ? "Nice find!" : "Perfect!")}
            </>
          ),
          type: hasWonPuzzle ? "bonus" : "success",
        });
        setAfterInput("");
        triggerSuccess();
        playSound("success");
        triggerHaptic("success");
        return;
      }

      // Step 2: Not in hardcoded list - try API validation
      setMessage({
        text: (
          <>
            <RefreshCw size={20} className="spin" /> Checking...
          </>
        ),
        type: "info",
      });

      const apiResult = await validateWithAPI(word, gameData.pivotWord, "after");
      console.log("API Result (after):", apiResult);

      if (apiResult.valid) {
        // AI approved this word!
        const newFound = [...foundAfter, word];
        setFoundAfter(newFound);
        setMessage({
          text: (
            <>
              <Sparkles size={20} /> {hasWonPuzzle 
                ? `Bonus! +${bonusWords + 1}` 
                : (apiResult.source === "ai_validated" ? "Nice find!" : "Perfect!")}
            </>
          ),
          type: hasWonPuzzle ? "bonus" : "success",
        });
        setAfterInput("");
        triggerSuccess();
        playSound("success");
        triggerHaptic("success");
      } else {
        // Wrong guess - only count in Standard/Hard mode
        if (settings.gameMode !== "casual") {
          setWrongGuesses((prev) => prev + 1);
        }
        setMessage({
          text: (
            <>
              <X size={20} /> Not a valid phrase
              {settings.gameMode !== "casual" && wrongGuesses < MAX_WRONG_GUESSES - 1 && (
                <span style={{ marginLeft: "8px", fontSize: "14px" }}>
                  ({MAX_WRONG_GUESSES - wrongGuesses - 1} left)
                </span>
              )}
            </>
          ),
          type: "error",
        });
        triggerShake();
        playSound("error");
        triggerHaptic("error");
      }
    }
  };

  const triggerShake = () => {
    setShake(true);
    setTimeout(() => setShake(false), 500);
  };

  const triggerSuccess = () => {
    const element = document.getElementById("pivot-word");
    if (element && settings.animationsEnabled) {
      element.style.transform = "scale(1.1)";
      setTimeout(() => (element.style.transform = "scale(1)"), 300);
    }
  };

  const triggerCelebration = () => {
    if (!settings.animationsEnabled) return;
    const particles = [];
    for (let i = 0; i < 50; i++) {
      particles.push({
        id: i,
        left: Math.random() * 100,
        delay: Math.random() * 0.5,
      });
    }
    setCelebrationParticles(particles);
    setTimeout(() => setCelebrationParticles([]), 3000);
  };

  const getRank = () => {
    if (hasWonPuzzle) {
      if (bonusWords >= 20)
        return { rank: "Legend", icon: <Crown size={24} />, emoji: "👑", color: "#9333ea" };
      if (bonusWords >= 10)
        return { rank: "Word Master", icon: <Medal size={24} />, emoji: "🏆", color: "#ca8a04" };
      if (bonusWords >= 5)
        return { rank: "Amazing", icon: <Star size={24} />, emoji: "⭐", color: "#f59e0b" };
      if (bonusWords >= 1)
        return { rank: "Great", icon: <ThumbsUp size={24} />, emoji: "👍", color: "#16a34a" };
      return { rank: "Solver", icon: <CheckCircle size={24} />, emoji: "✅", color: "#2563eb" };
    }
    if (foundWords >= 7)
      return { rank: "Almost There", icon: <Target size={24} />, emoji: "🎯", color: "#f59e0b" };
    if (foundWords >= 5)
      return { rank: "Halfway", icon: <Target size={24} />, emoji: "🎯", color: "#2563eb" };
    if (foundWords >= 3)
      return { rank: "Warming Up", icon: <Target size={24} />, emoji: "🎯", color: "#6b7280" };
    return { rank: "Getting Started", icon: <Target size={24} />, emoji: "🎯", color: "#6b7280" };
  };

  // Calculate theme key and get theme object ONCE
  const baseTheme = settings.theme;
  const themeKey = settings.highContrast ? `${baseTheme}HC` : baseTheme;
  const theme = themes[themeKey] || themes.light;

  // SINGLE getTitleColor function that works correctly
  const getTitleColor = () => {
    switch (settings.theme) {
      case "dark":
        return "#ffffff";
      case "light":
        return "#4f46e5";
      case "spring":
        return "#16a34a";
      case "summer":
        return "#ea580c";
      case "fall":
        return "#dc2626";
      case "winter":
        return "#0284c7";
      default:
        return "#1f2937";
    }
  };

  const showNextHint = () => {
    if (currentHintIndex < gameData.hints.length - 1) {
      setCurrentHintIndex(currentHintIndex + 1);
    }
  };

  const getProgressPercentage = () => {
    return Math.min((foundWords / TARGET_WORDS) * 100, 100);
  };

  const getDifficultyColor = () => {
    switch (gameData.difficulty) {
      case "easy":
        return { bg: "#dcfce7", text: "#16a34a" };
      case "medium":
        return { bg: "#fef3c7", text: "#ca8a04" };
      case "hard":
        return { bg: "#fee2e2", text: "#dc2626" };
      default:
        return { bg: "#f3f4f6", text: "#6b7280" };
    }
  };

  const generateShareText = () => {
    const rankInfo = getRank();
    const isComplete = foundWords >= totalWords;
    const isCasual = settings.gameMode === "casual";
    const isHard = settings.gameMode === "hard";

    // Status line
    let statusText;
    if (isComplete) {
      statusText = `${rankInfo.emoji} PERFECT!`;
    } else if (isGameOver) {
      statusText = "💔 Game Over";
    } else {
      statusText = `${rankInfo.emoji} ${rankInfo.rank}`;
    }

    // Mode indicator
    const modeIcon = isCasual ? "☕" : isHard ? "🔥" : "🎯";
    const modeName = isCasual ? "Casual" : isHard ? "Hard" : "Standard";

    // Stats line
    let statsLine = "";
    if (!isCasual) {
      statsLine = `⏱️ ${formatTime(timeElapsed)}`;
      if (wrongGuesses > 0 || isGameOver) {
        statsLine += ` • ❌ ${wrongGuesses}`;
      }
    }

    // Emoji grid - filled squares based on completion
    const beforeCount = foundBefore.length;
    const beforeTotal = gameData.validBefore.length;
    const afterCount = foundAfter.length;
    const afterTotal = gameData.validAfter.length;

    const beforeFilled = Math.round((beforeCount / beforeTotal) * 5);
    const afterFilled = Math.round((afterCount / afterTotal) * 5);

    const beforeGrid = "🟩".repeat(beforeFilled) + "⬜".repeat(5 - beforeFilled);
    const afterGrid = "🟪".repeat(afterFilled) + "⬜".repeat(5 - afterFilled);

    // Build share text
    let shareText = `Pivot #${gameData.puzzleNumber} ${statusText}\n`;
    shareText += `${modeIcon} ${modeName}\n`;
    if (statsLine) {
      shareText += `${statsLine}\n`;
    }
    shareText += `\n`;
    shareText += `⬆️ ${beforeGrid} ${beforeCount}/${beforeTotal}\n`;
    shareText += `⬇️ ${afterGrid} ${afterCount}/${afterTotal}\n`;
    shareText += `\npivotpuzzle.com`;

    return shareText;
  };

  const copyShareText = () => {
    const shareText = generateShareText();
    navigator.clipboard.writeText(shareText).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    });
  };

  const startGame = () => {
    setShowTutorial(false);
    setIsPlaying(true);
  };

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 2000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const rankInfo = getRank();
  const diffColor = getDifficultyColor();

  // Keyboard layout
  const keyboardRows = [
    ["Q", "W", "E", "R", "T", "Y", "U", "I", "O", "P"],
    ["A", "S", "D", "F", "G", "H", "J", "K", "L"],
    ["ENTER", "Z", "X", "C", "V", "B", "N", "M", "DEL"],
  ];

  const handleKeyPress = async (key) => {
    if (isGameOver || foundWords >= totalWords || !isPlaying) return;

    const currentInput = activeInput === "before" ? beforeInput : afterInput;
    const setInput = activeInput === "before" ? setBeforeInput : setAfterInput;

    if (key === "DEL") {
      setInput(currentInput.slice(0, -1));
    } else if (key === "ENTER") {
      if (currentInput.trim()) {
        await checkWord(currentInput, activeInput);
      }
    } else {
      setInput(currentInput + key.toLowerCase());
    }
  };

  const KeyboardButton = ({ keyVal, wide }) => {
    const isEnter = keyVal === "ENTER";
    const isDel = keyVal === "DEL";

    return (
      <button
        onClick={() => handleKeyPress(keyVal)}
        style={{
          padding: wide ? "16px 12px" : "16px 6px",
          minWidth: wide ? "65px" : "32px",
          height: "52px",
          background: isEnter
            ? activeInput === "before"
              ? "linear-gradient(135deg, #22c55e, #16a34a)"
              : "linear-gradient(135deg, #9333ea, #7c3aed)"
            : isDel
              ? "#f87171"
              : theme.uiBg,
          color: isEnter || isDel ? "white" : theme.uiText,
          fontWeight: "bold",
          fontSize: wide ? "12px" : "16px",
          borderRadius: "8px",
          border: `1px solid ${theme.text}20`,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "all 0.1s",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        }}
      >
        {isDel ? <X size={18} /> : isEnter ? <Check size={18} /> : keyVal}
      </button>
    );
  };

  // Tutorial Carousel Component

  // ============================================
  // SUPABASE AUTH LOGIC
  // ============================================

  // Listen for auth state changes
  useEffect(() => {
    // Get initial session
    console.log('Pivot Game - Supabase Integration Loaded');
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setAuthLoading(false);

      // If user is logged in, sync stats from cloud
      if (session?.user) {
        loadCloudStats(session.user.id);
      }
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);

        if (event === 'SIGNED_IN' && session?.user) {
          // Load or merge stats when user signs in
          await loadCloudStats(session.user.id);
        }

        if (event === 'SIGNED_OUT') {
          // Keep local stats, just disconnect from cloud
          setIsLoggedIn(false);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  // Sign up with email
  const handleSignUp = async () => {
    setAuthError(null);

    if (!emailInput || !passwordInput) {
      setAuthError('Please enter email and password');
      return;
    }

    if (passwordInput.length < 6) {
      setAuthError('Password must be at least 6 characters');
      return;
    }

    const { data, error } = await supabase.auth.signUp({
      email: emailInput,
      password: passwordInput,
    });

    if (error) {
      setAuthError(error.message);
    } else {
      // Check if email confirmation is required
      if (data?.user?.identities?.length === 0) {
        setAuthError('This email is already registered. Try logging in.');
      } else {
        setAuthError(null);
        alert('Check your email for a confirmation link!');
        setShowLoginModal(false);
      }
    }
  };

  // Login with email
  const handleLogin = async () => {
    setAuthError(null);

    if (!emailInput || !passwordInput) {
      setAuthError('Please enter email and password');
      return;
    }

    const { data, error } = await supabase.auth.signInWithPassword({
      email: emailInput,
      password: passwordInput,
    });

    if (error) {
      setAuthError(error.message);
    } else {
      setIsLoggedIn(true);
      setShowLoginModal(false);
      setEmailInput('');
      setPasswordInput('');
    }
  };

  // Login with Google
  const handleGoogleLogin = async () => {
    setAuthError(null);

    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin,
      },
    });

    if (error) {
      setAuthError(error.message);
    }
  };

  // Logout
  const handleLogout = async () => {
    await supabase.auth.signOut();
    setIsLoggedIn(false);
    setUser(null);
    setSession(null);
  };

  // Load stats from cloud
  const loadCloudStats = async (userId) => {
    try {
      const { data, error } = await supabase
        .from('user_stats')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error loading stats:', error);
        return;
      }

      if (data) {
        // Merge cloud stats with local stats (take the best of both)
        const localStats = loadStats();
        const mergedStats = {
          gamesPlayed: Math.max(localStats.gamesPlayed, data.games_played || 0),
          gamesWon: Math.max(localStats.gamesWon, data.games_won || 0),
          currentStreak: data.current_streak || localStats.currentStreak,
          maxStreak: Math.max(localStats.maxStreak, data.max_streak || 0),
          totalTime: Math.max(localStats.totalTime, data.total_time || 0),
          bestTime: data.best_time && localStats.bestTime
            ? Math.min(data.best_time, localStats.bestTime)
            : data.best_time || localStats.bestTime,
          averageTime: data.average_time || localStats.averageTime,
          lastPlayedDate: data.last_played_date || localStats.lastPlayedDate,
          profile: localStats.profile,
        };

        setStats(mergedStats);
        saveStats(mergedStats);
        setIsLoggedIn(true);
      }
    } catch (err) {
      console.error('Error loading cloud stats:', err);
    }
  };

  // Save stats to cloud
  const saveCloudStats = async (newStats) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('user_stats')
        .upsert({
          user_id: user.id,
          games_played: newStats.gamesPlayed,
          games_won: newStats.gamesWon,
          current_streak: newStats.currentStreak,
          max_streak: newStats.maxStreak,
          total_time: newStats.totalTime,
          best_time: newStats.bestTime,
          average_time: newStats.averageTime,
          last_played_date: newStats.lastPlayedDate,
          updated_at: new Date().toISOString(),
        }, {
          onConflict: 'user_id',
        });

      if (error) {
        console.error('Error saving to cloud:', error);
      }
    } catch (err) {
      console.error('Error saving cloud stats:', err);
    }
  };

  // Save game session to cloud
  const saveGameSession = async (won) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('game_sessions')
        .upsert({
          user_id: user.id,
          puzzle_number: gameData.puzzleNumber,
          puzzle_date: new Date().toISOString().split('T')[0],
          completed: true,
          won: won,
          time_elapsed: timeElapsed,
          wrong_guesses: wrongGuesses,
          game_mode: settings.gameMode,
          found_before: foundBefore,
          found_after: foundAfter,
          updated_at: new Date().toISOString(),
        }, {
          onConflict: 'user_id,puzzle_number',
        });

      if (error) {
        console.error('Error saving game session:', error);
      }
    } catch (err) {
      console.error('Error saving session:', err);
    }
  };
  const styles = {

    container: {
      minHeight: "100vh",
      background: theme.container,
      padding: "20px",
      fontFamily:
        '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      color: theme.text,
      userSelect: "none", // Prevent text selection
      WebkitTapHighlightColor: "transparent", // Remove tap highlight on mobile
    },
    modal: {
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.5)",
      backdropFilter: "blur(8px)",
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "center",
      zIndex: 1000,
      padding: "20px",
      overflowY: "auto",
    },
    modalContent: {
      background: theme.gameBg,
      borderRadius: "24px",
      boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
      maxWidth: "500px",
      width: "100%",
      padding: "24px",
      animation: "scaleIn 0.3s ease-out",
      color: theme.text,
      marginTop: "10vh",
      maxHeight: "85vh",
      overflowY: "auto",
    },
    button: {
      width: "100%",
      padding: "16px",
      background: theme.pivotBg,
      color:
        theme.buttonText || (theme.text === "#ffffff" ? "#ffffff" : "white"),
      fontWeight: "bold",
      fontSize: "18px",
      borderRadius: "16px",
      border: "none",
      cursor: "pointer",
      boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)",
      transition: "all 0.2s",
    },
    input: {
      width: "100%",
      padding: "16px 24px",
      fontSize: "20px",
      borderRadius: "16px",
      border: "2px solid #e5e7eb",
      outline: "none",
      transition: "all 0.2s",
      boxSizing: "border-box",
      background: theme.uiBg,
      color: theme.uiText,
    },
    pivotWord: {
      fontSize: "72px",
      fontWeight: "900",
      padding: "32px 48px",
      background: theme.pivotBg,
      color:
        theme.buttonText || (theme.text === "#ffffff" ? "#ffffff" : "white"),
      borderRadius: "24px",
      boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
      display: "inline-block",
      transition: "transform 0.2s",
      cursor: "pointer",
    },
    gameContainer: {
      background: theme.gameBg,
      backdropFilter: "blur(16px)",
      borderRadius: "24px",
      boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
      padding: "32px",
      border: "1px solid rgba(255,255,255,0.5)",
    },
    progressBar: {
      height: "12px",
      background: "#e5e7eb",
      borderRadius: "9999px",
      overflow: "hidden",
    },
    progressFill: {
      height: "100%",
      background: theme.pivotBg,
      transition: "width 0.5s ease-out",
      width: `${getProgressPercentage()}%`,
    },
    foundWordCard: {
      background: "white",
      padding: "12px 16px",
      borderRadius: "12px",
      marginBottom: "8px",
      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
      transition: "all 0.2s",
      cursor: "default",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      textTransform: "capitalize",
      fontWeight: "500",
    },
    particle: {
      position: "fixed",
      top: "-20px",
      fontSize: "24px",
      pointerEvents: "none",
      zIndex: 1000,
      animation: "fall 3s linear forwards",
    },
    themeButton: {
      padding: "12px 16px",
      background: theme.gameBg,
      border: "2px solid #e5e7eb",
      borderRadius: "12px",
      cursor: "pointer",
      fontSize: "24px",
      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
      transition: "all 0.2s",
      color: theme.text,
    },
    infoBox: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      background: theme.uiBg,
      color: theme.uiText,
      padding: "8px 16px",
      borderRadius: "9999px",
      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    },
  };

  const tutorialContentStyle = {
    marginTop: "16px",
    marginBottom: "16px",
  };

  const sectionStyle = {
    display: "flex",
    gap: "12px",
    marginBottom: "12px",
  };

  const exampleBoxStyle = {
    background: "linear-gradient(to right, #e0e7ff, #f3e8ff)",
    borderRadius: "12px",
    padding: "12px",
    border: "2px solid #6366f1",
    marginBottom: "16px",
  };

  return (
    <div style={styles.container}>
      <style>{`
        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        @keyframes fall {
          to {
            transform: translateY(100vh) rotate(360deg);
            opacity: 0;
          }
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-10px); }
          75% { transform: translateX(10px); }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .spin {
          animation: spin 1s linear infinite;
        }
        button:hover {
          transform: scale(1.02);
          box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1);
        }
        button:active {
          transform: scale(0.98);
        }
        input:focus {
          border-color: #6366f1;
          box-shadow: 0 10px 15px -3px rgba(99,102,241,0.1);
        }
        
        /* iOS Scrolling Fixes */
        * {
          -webkit-tap-highlight-color: transparent;
        }
        html, body {
          overscroll-behavior: none;
          -webkit-overflow-scrolling: touch;
        }
        .modal-scroll {
          -webkit-overflow-scrolling: touch;
          overscroll-behavior: contain;
        }
        
        /* Mobile Responsive Pivot Word */
        .pivot-word-responsive {
          font-size: clamp(32px, 10vw, 72px) !important;
          padding: clamp(16px, 4vw, 32px) clamp(24px, 6vw, 48px) !important;
          word-break: break-word;
          max-width: 100%;
        }
        
        /* Mobile Input Container */
        .input-container {
          display: flex;
          gap: 8px;
          align-items: stretch;
        }
        .input-container input {
          flex: 1;
          min-width: 0;
        }
        .submit-btn {
          padding: 16px 20px;
          background: linear-gradient(135deg, #6366f1, #a855f7);
          color: white;
          font-weight: bold;
          font-size: 16px;
          border-radius: 16px;
          border: none;
          cursor: pointer;
          white-space: nowrap;
          flex-shrink: 0;
        }
        .submit-btn:disabled {
          background: #d1d5db;
          cursor: not-allowed;
        }
        
        @media (max-width: 768px) {
          .grid-container {
            grid-template-columns: 1fr !important;
          }
          .info-boxes {
            flex-direction: column !important;
            gap: 8px !important;
          }
          .header-buttons {
            gap: 4px !important;
          }
          .header-buttons button {
            padding: 8px 12px !important;
          }
          input {
            font-size: 16px !important;
          }
        }
        
        @media (max-width: 400px) {
          .submit-btn {
            padding: 16px 12px;
            font-size: 14px;
          }
        }
      `}</style>

      {/* Welcome Page */}
      {showWelcome && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: theme.container,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000,
            padding: "20px",
          }}
        >
          <div
            style={{
              maxWidth: "400px",
              width: "100%",
              textAlign: "center",
            }}
          >
            {/* Logo/Icon */}
            <div
              style={{
                width: "80px",
                height: "80px",
                background: theme.pivotBg,
                borderRadius: "20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 16px",
                boxShadow: "0 10px 25px -5px rgba(99, 102, 241, 0.4)",
              }}
            >
              <span style={{ fontSize: "40px", color: "white", fontWeight: "900" }}>
                P
              </span>
            </div>

            {/* Title */}
            <h1
              style={{
                fontSize: "48px",
                fontWeight: "900",
                color: getTitleColor(),
                marginBottom: "8px",
                letterSpacing: "-1px",
              }}
            >
              Pivot
            </h1>

            {/* Beta Badge */}
            <div
              style={{
                display: "inline-block",
                background: "linear-gradient(to right, #f59e0b, #ef4444)",
                color: "white",
                padding: "4px 12px",
                borderRadius: "9999px",
                fontSize: "11px",
                fontWeight: "bold",
                marginBottom: "24px",
              }}
            >
              BETA
            </div>

            {/* Welcome Message */}
            <div
              style={{
                background: theme.text === "#ffffff"
                  ? "rgba(255,255,255,0.1)"
                  : "rgba(255,255,255,0.9)",
                borderRadius: "20px",
                padding: "24px",
                marginBottom: "24px",
                boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                border: theme.text === "#ffffff"
                  ? "1px solid rgba(255,255,255,0.2)"
                  : "none",
              }}
            >
              <h2
                style={{
                  fontSize: "24px",
                  fontWeight: "700",
                  color: theme.text,
                  marginBottom: "8px",
                }}
              >
                {stats.gamesPlayed > 0 ? "Welcome Back!" : "Welcome!"}
              </h2>

              {stats.gamesPlayed > 0 ? (
                <div style={{ marginBottom: "16px" }}>
                  <p
                    style={{
                      color: theme.text === "#ffffff"
                        ? "rgba(255,255,255,0.7)"
                        : "#6b7280",
                      marginBottom: "8px",
                    }}
                  >
                    {stats.currentStreak > 0 ? (
                      <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "4px" }}>
                        <Flame size={18} color="#f59e0b" /> {stats.currentStreak} day streak! Keep it going.
                      </span>
                    ) : (
                      <>Welcome back! You've played {stats.gamesPlayed} games.</>
                    )}
                  </p>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      gap: "16px",
                      fontSize: "12px",
                      color: theme.text === "#ffffff" ? "rgba(255,255,255,0.5)" : "#9ca3af",
                    }}
                  >
                    <span>{stats.gamesWon} wins</span>
                    <span>•</span>
                    <span>Best: {stats.bestTime ? formatTime(stats.bestTime) : "--"}</span>
                  </div>
                </div>
              ) : (
                <p
                  style={{
                    color: theme.text === "#ffffff"
                      ? "rgba(255,255,255,0.7)"
                      : "#6b7280",
                    marginBottom: "16px",
                  }}
                >
                  Find words that connect before or after the pivot word.
                </p>
              )}

              {/* Buttons */}
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                <button
                  onClick={() => {
                    setShowWelcome(false);
                    if (isNewUser) {
                      setShowTutorial(true);
                    } else {
                      setIsPlaying(true);
                    }
                  }}
                  style={{
                    ...styles.button,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                  }}
                >
                  <Play size={20} /> Play
                </button>

                {!user && (
                  <button
                    onClick={() => setShowLoginModal(true)}
                    style={{
                      ...styles.button,
                      background: "white",
                      color: "#374151",
                      border: "2px solid #e5e7eb",
                    }}
                  >
                    Log In
                  </button>
                )}
              </div>
            </div>

            {/* Puzzle Info */}
            <div
              style={{
                color: theme.text === "#ffffff"
                  ? "rgba(255,255,255,0.7)"
                  : "#6b7280",
                fontSize: "14px",
              }}
            >
              <p style={{ marginBottom: "4px" }}>
                {new Date().toLocaleDateString("en-US", {
                  weekday: "long",
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </p>
              <p
                style={{
                  marginBottom: "4px",
                  fontWeight: "600",
                  color: theme.text,
                }}
              >
                Puzzle #{gameData.puzzleNumber}
              </p>
              <p
                style={{
                  fontSize: "12px",
                  color: theme.text === "#ffffff"
                    ? "rgba(255,255,255,0.5)"
                    : "#9ca3af",
                }}
              >
                Created by Jorge Contreras Mendez
              </p>
            </div>



            {/* Settings shortcut */}

          </div>
        </div>
      )
      }

      {/* Login Modal */}
      {showLoginModal && (
        <div style={{ ...styles.modal, zIndex: 3000 }} onClick={() => setShowLoginModal(false)}>
          <div
            style={{
              ...styles.modalContent,
              background: theme.uiBg || theme.gameBg,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2
              style={{
                fontSize: "28px",
                fontWeight: "900",
                color: theme.text,
                textAlign: "center",
                marginBottom: "8px",
              }}
            >
              {authMode === 'login' ? 'Welcome Back!' : 'Create Account'}
            </h2>

            <p style={{ textAlign: "center", color: theme.text + "80", marginBottom: "24px" }}>
              {authMode === 'login'
                ? 'Sign in to sync your stats across devices'
                : 'Sign up to save your progress'}
            </p>

            {authError && (
              <div style={{
                background: "#fee2e2",
                border: "1px solid #ef4444",
                borderRadius: "8px",
                padding: "12px",
                marginBottom: "16px",
                color: "#dc2626",
                fontSize: "14px",
              }}>
                {authError}
              </div>
            )}

            <div style={{ marginTop: "16px" }}>
              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: theme.text,
                  marginBottom: "8px",
                }}
              >
                Email address
              </label>
              <input
                type="email"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                placeholder="you@example.com"
                style={{
                  ...styles.input,
                  background: theme.uiBg,
                  color: theme.uiText,
                  borderColor: theme.text === "#ffffff" ? "rgba(255,255,255,0.3)" : "#e5e7eb",
                  marginBottom: "16px",
                }}
              />

              <label
                style={{
                  display: "block",
                  fontSize: "14px",
                  fontWeight: "600",
                  color: theme.text,
                  marginBottom: "8px",
                }}
              >
                Password
              </label>
              <input
                type="password"
                value={passwordInput}
                onChange={(e) => setPasswordInput(e.target.value)}
                placeholder="••••••••"
                style={{
                  ...styles.input,
                  background: theme.uiBg,
                  color: theme.uiText,
                  borderColor: theme.text === "#ffffff" ? "rgba(255,255,255,0.3)" : "#e5e7eb",
                  marginBottom: "16px",
                }}
              />

              <button
                onClick={authMode === 'login' ? handleLogin : handleSignUp}
                style={{
                  ...styles.button,
                  background: theme.pivotBg,
                  marginBottom: "12px",
                }}
              >
                {authMode === 'login' ? 'Sign In' : 'Create Account'}
              </button>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "16px",
                  margin: "16px 0",
                }}
              >
                <div
                  style={{
                    flex: 1,
                    height: "1px",
                    background: theme.text === "#ffffff" ? "rgba(255,255,255,0.2)" : "#e5e7eb",
                  }}
                />
                <span
                  style={{
                    color: theme.text === "#ffffff" ? "rgba(255,255,255,0.6)" : "#9ca3af",
                    fontSize: "14px",
                  }}
                >
                  or
                </span>
                <div
                  style={{
                    flex: 1,
                    height: "1px",
                    background: theme.text === "#ffffff" ? "rgba(255,255,255,0.2)" : "#e5e7eb",
                  }}
                />
              </div>

              {/* Google Sign In Button */}
              <button
                onClick={handleGoogleLogin}
                style={{
                  width: "100%",
                  padding: "16px",
                  background: "#ffffff",
                  color: "#374151",
                  fontWeight: "bold",
                  fontSize: "16px",
                  borderRadius: "16px",
                  border: "2px solid #e5e7eb",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "12px",
                  marginBottom: "16px",
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                Continue with Google
              </button>

              {/* Toggle between login and signup */}
              <p style={{ textAlign: "center", fontSize: "14px", color: theme.text + "80" }}>
                {authMode === 'login' ? "Don't have an account? " : "Already have an account? "}
                <button
                  onClick={() => {
                    setAuthMode(authMode === 'login' ? 'signup' : 'login');
                    setAuthError(null);
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#6366f1",
                    cursor: "pointer",
                    fontWeight: "600",
                    padding: 0,
                  }}
                >
                  {authMode === 'login' ? 'Sign Up' : 'Sign In'}
                </button>
              </p>
            </div>

            <button
              onClick={() => {
                setShowLoginModal(false);
                setAuthError(null);
              }}
              style={{
                width: "100%",
                padding: "12px",
                background: "none",
                color: theme.text === "#ffffff" ? "rgba(255,255,255,0.6)" : "#6b7280",
                border: "none",
                cursor: "pointer",
                marginTop: "16px",
                fontSize: "14px",
              }}
            >
              Maybe Later
            </button>
          </div>
        </div>
      )}

      {/* Suggest a Word Modal */}
      {
        showSuggestModal && (
          <div style={styles.modal} onClick={() => setShowSuggestModal(false)}>
            <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
              <h2
                style={{
                  fontSize: "28px",
                  fontWeight: "900",
                  color: getTitleColor(),
                  textAlign: "center",
                  marginBottom: "8px",
                }}
              >
                <MessageSquare size={28} /> Suggest a Word
              </h2>

              {suggestionSubmitted ? (
                <div style={{ textAlign: "center", padding: "24px 0" }}>
                  <div
                    style={{
                      width: "60px",
                      height: "60px",
                      background: "#dcfce7",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 16px",
                    }}
                  >
                    <Check size={32} color="#16a34a" />
                  </div>
                  <h3
                    style={{
                      fontSize: "20px",
                      fontWeight: "bold",
                      color: theme.text,
                      marginBottom: "8px",
                    }}
                  >
                    Thanks for your suggestion!
                  </h3>
                  <p style={{ color: theme.text + "80", marginBottom: "24px" }}>
                    We'll review "{suggestionWord}" and add it if it's a valid phrase.
                  </p>
                  <button
                    onClick={() => {
                      setShowSuggestModal(false);
                      setSuggestionSubmitted(false);
                      setSuggestionWord("");
                    }}
                    style={styles.button}
                  >
                    Close
                  </button>
                </div>
              ) : (
                <>
                  <p
                    style={{
                      textAlign: "center",
                      color: theme.text + "80",
                      marginBottom: "24px",
                    }}
                  >
                    Think we missed a valid word for "{gameData.pivotWord}"? Let us know!
                  </p>

                  <div style={{ marginBottom: "16px" }}>
                    <label
                      style={{
                        display: "block",
                        fontSize: "14px",
                        fontWeight: "600",
                        color: theme.text,
                        marginBottom: "8px",
                      }}
                    >
                      Word type
                    </label>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <button
                        onClick={() => setSuggestionType("before")}
                        style={{
                          flex: 1,
                          padding: "12px",
                          borderRadius: "12px",
                          border: "2px solid",
                          borderColor:
                            suggestionType === "before" ? "#16a34a" : "#e5e7eb",
                          background:
                            suggestionType === "before" ? "#dcfce7" : "white",
                          color: suggestionType === "before" ? "#16a34a" : "#6b7280",
                          fontWeight: "600",
                          cursor: "pointer",
                        }}
                      >
                        ___ {gameData.pivotWord}
                      </button>
                      <button
                        onClick={() => setSuggestionType("after")}
                        style={{
                          flex: 1,
                          padding: "12px",
                          borderRadius: "12px",
                          border: "2px solid",
                          borderColor:
                            suggestionType === "after" ? "#9333ea" : "#e5e7eb",
                          background:
                            suggestionType === "after" ? "#f3e8ff" : "white",
                          color: suggestionType === "after" ? "#9333ea" : "#6b7280",
                          fontWeight: "600",
                          cursor: "pointer",
                        }}
                      >
                        {gameData.pivotWord} ___
                      </button>
                    </div>
                  </div>

                  <div style={{ marginBottom: "16px" }}>
                    <label
                      style={{
                        display: "block",
                        fontSize: "14px",
                        fontWeight: "600",
                        color: theme.text,
                        marginBottom: "8px",
                      }}
                    >
                      Your word
                    </label>
                    <input
                      type="text"
                      placeholder={
                        suggestionType === "before"
                          ? `e.g., "spell" for "spell ${gameData.pivotWord.toLowerCase()}"`
                          : `e.g., "iron" for "${gameData.pivotWord.toLowerCase()} iron"`
                      }
                      value={suggestionWord}
                      onChange={(e) => setSuggestionWord(e.target.value)}
                      style={styles.input}
                    />
                  </div>

                  <div
                    style={{
                      background: "#fef3c7",
                      border: "1px solid #fbbf24",
                      borderRadius: "12px",
                      padding: "12px",
                      marginBottom: "24px",
                    }}
                  >
                    <p
                      style={{
                        fontSize: "13px",
                        color: "#92400e",
                        margin: 0,
                      }}
                    >
                      <Lightbulb size={16} /> Only suggest words that form a <strong>real phrase</strong>{" "}
                      (like "cast iron" or "typecast"). Random words won't be added.
                    </p>
                  </div>

                  <button
                    onClick={() => {
                      if (suggestionWord.trim()) {
                        console.log(
                          `Suggestion: ${suggestionType === "before" ? suggestionWord + " " + gameData.pivotWord : gameData.pivotWord + " " + suggestionWord}`
                        );
                        setSuggestionSubmitted(true);
                      }
                    }}
                    disabled={!suggestionWord.trim()}
                    style={{
                      ...styles.button,
                      background: suggestionWord.trim() ? theme.pivotBg : "#d1d5db",
                      cursor: suggestionWord.trim() ? "pointer" : "not-allowed",
                    }}
                  >
                    Submit Suggestion
                  </button>

                  <button
                    onClick={() => setShowSuggestModal(false)}
                    style={{
                      width: "100%",
                      padding: "12px",
                      background: "none",
                      color: theme.text + "80",
                      border: "none",
                      cursor: "pointer",
                      marginTop: "12px",
                      fontSize: "14px",
                    }}
                  >
                    Cancel
                  </button>
                </>
              )}
            </div>
          </div>
        )
      }

      {/* Settings Modal - Tabbed */}
      {showSettingsModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: theme.container,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000,
            padding: "20px",
          }}
        >
          <SettingsTabs
            theme={theme}
            settings={settings}
            setSettings={setSettings}
            saveSettings={saveSettings}
            showKeyboard={showKeyboard}
            setShowKeyboard={setShowKeyboard}
            getTitleColor={getTitleColor}
            onClose={() => setShowSettingsModal(false)}
            playSound={playSound}
          />
        </div>
      )}

      {/* Victory Modal */}
      {
        showVictoryModal && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.7)",
              backdropFilter: "blur(8px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
              padding: "20px",
            }}
          >
            <div
              style={{
                background: theme.pivotBg,
                borderRadius: "24px",
                padding: "32px",
                maxWidth: "400px",
                width: "100%",
                textAlign: "center",
                boxShadow: "0 25px 50px -12px rgba(0,0,0,0.5)",
                animation: "scaleIn 0.3s ease-out",
              }}
            >
              <div style={{ marginBottom: "16px", display: "flex", justifyContent: "center" }}>
                <Trophy size={64} color="#fbbf24" fill="#fbbf24" />
              </div>

              <h2
                style={{
                  fontSize: "32px",
                  fontWeight: "900",
                  color: "white",
                  marginBottom: "8px",
                }}
              >
                PERFECT!
              </h2>

              <p
                style={{
                  color: "rgba(255,255,255,0.9)",
                  fontSize: "18px",
                  marginBottom: "8px",
                }}
              >
                You found all {totalWords} words!
              </p>

              <p
                style={{
                  color: "rgba(255,255,255,0.7)",
                  fontSize: "16px",
                  marginBottom: "24px",
                }}
              >
                {settings.gameMode === "casual" ? (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
                    <Zap size={20} fill="currentColor" /> Casual Mode
                  </div>
                ) : (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
                    <Clock size={20} /> {formatTime(timeElapsed)}
                    {wrongGuesses > 0 && (
                      <>
                        <span style={{ margin: "0 4px" }}>•</span>
                        <XCircle size={20} color="#ef4444" /> {wrongGuesses} mistakes
                      </>
                    )}
                  </div>
                )}
              </p>

              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: "24px",
                  marginBottom: "24px",
                }}
              >
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: "28px", fontWeight: "bold", color: "white" }}>
                    {stats.currentStreak}
                  </div>
                  <div style={{ fontSize: "12px", color: "rgba(255,255,255,0.7)" }}>
                    Streak
                  </div>
                </div>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: "28px", fontWeight: "bold", color: "white" }}>
                    {stats.gamesWon}
                  </div>
                  <div style={{ fontSize: "12px", color: "rgba(255,255,255,0.7)" }}>
                    Wins
                  </div>
                </div>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: "28px", fontWeight: "bold", color: "white" }}>
                    {stats.bestTime ? formatTime(stats.bestTime) : "--"}
                  </div>
                  <div style={{ fontSize: "12px", color: "rgba(255,255,255,0.7)" }}>
                    Best
                  </div>
                </div>
              </div>

              <button
                onClick={() => setShowShareModal(true)}
                style={{
                  width: "100%",
                  padding: "16px",
                  background: "white",
                  color: "#6366f1",
                  fontWeight: "bold",
                  fontSize: "18px",
                  borderRadius: "16px",
                  border: "none",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  marginBottom: "12px",
                }}
              >
                <Share2 size={20} /> Share Results
              </button>

              <button
                onClick={() => setShowVictoryModal(false)}
                style={{
                  width: "100%",
                  padding: "12px",
                  background: "rgba(255,255,255,0.2)",
                  color: "white",
                  fontWeight: "600",
                  fontSize: "14px",
                  borderRadius: "12px",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                View Puzzle
              </button>
            </div>
          </div>
        )
      }
      {/* Game Over Modal */}
      {
        showGameOverModal && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.7)",
              backdropFilter: "blur(8px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
              padding: "20px",
            }}
          >
            <div
              style={{
                background: "linear-gradient(135deg, #ef4444, #dc2626)",
                borderRadius: "24px",
                padding: "32px",
                maxWidth: "400px",
                width: "100%",
                textAlign: "center",
                boxShadow: "0 25px 50px -12px rgba(0,0,0,0.5)",
                animation: "scaleIn 0.3s ease-out",
              }}
            >
              <div style={{ fontSize: "64px", marginBottom: "16px" }}>
                <X size={64} color="white" />
              </div>
              <h2 style={{ fontSize: "32px", fontWeight: "900", color: "white", marginBottom: "8px" }}>
                Game Over
              </h2>
              <p style={{ color: "rgba(255,255,255,0.9)", fontSize: "18px", marginBottom: "8px" }}>
                You used all 5 guesses
              </p>
              <p style={{ color: "rgba(255,255,255,0.7)", fontSize: "16px", marginBottom: "24px" }}>
                Found {foundWords}/{totalWords} words
              </p>
              <div style={{ display: "flex", justifyContent: "center", gap: "24px", marginBottom: "24px" }}>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: "28px", fontWeight: "bold", color: "white" }}>
                    {foundBefore.length}/{gameData.validBefore.length}
                  </div>
                  <div style={{ fontSize: "12px", color: "rgba(255,255,255,0.7)" }}>Before</div>
                </div>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: "28px", fontWeight: "bold", color: "white" }}>
                    {foundAfter.length}/{gameData.validAfter.length}
                  </div>
                  <div style={{ fontSize: "12px", color: "rgba(255,255,255,0.7)" }}>After</div>
                </div>
              </div>
              <button
                onClick={() => setShowShareModal(true)}
                style={{
                  width: "100%",
                  padding: "16px",
                  background: "white",
                  color: "#ef4444",
                  fontWeight: "bold",
                  fontSize: "18px",
                  borderRadius: "16px",
                  border: "none",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  marginBottom: "12px",
                }}
              >
                <Share2 size={20} /> Share Results
              </button>
              <button
                onClick={() => setShowGameOverModal(false)}
                style={{
                  width: "100%",
                  padding: "12px",
                  background: "rgba(255,255,255,0.2)",
                  color: "white",
                  fontWeight: "600",
                  fontSize: "14px",
                  borderRadius: "12px",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                View Puzzle
              </button>
            </div>
          </div>
        )
      }

      {/* Tutorial Modal */}
      {/* Tutorial Modal - Carousel */}
      {showTutorial && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: theme.container,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000,
            padding: "20px",
          }}
        >
          <TutorialCarousel
            theme={theme}
            settings={settings}
            getTitleColor={getTitleColor}
            onComplete={startGame}
          />
        </div>
      )}

      {/* Stats Modal - Fullscreen */}
      {
        showStatsModal && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: theme.container,
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "center",
              zIndex: 2000,
              padding: "20px",
              overflowY: "auto",
              WebkitOverflowScrolling: "touch",
            }}
          >
            <div
              style={{
                background: theme.gameBg,
                borderRadius: "24px",
                boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
                maxWidth: "500px",
                width: "100%",
                padding: "32px",
                color: theme.text,
                marginTop: "20px",
                marginBottom: "20px",
              }}
            >
              <button
                onClick={() => setShowStatsModal(false)}
                style={{
                  background: "none",
                  border: "none",
                  color: theme.text,
                  cursor: "pointer",
                  fontSize: "16px",
                  marginBottom: "16px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: 0,
                }}
              >
                <ArrowLeft size={20} /> Back
              </button>
              <h2
                style={{
                  fontSize: "36px",
                  fontWeight: "900",
                  color: getTitleColor(),
                  textAlign: "center",
                  marginBottom: "8px",
                }}
              >
                Your Statistics
              </h2>

              <div style={{ marginTop: "24px" }}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "16px",
                    marginBottom: "24px",
                  }}
                >
                  <div
                    style={{
                      textAlign: "center",
                      padding: "16px",
                      background: theme.cardBefore,
                      borderRadius: "12px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "32px",
                        fontWeight: "bold",
                        color: theme.text,
                      }}
                    >
                      {stats.gamesPlayed}
                    </div>
                    <div style={{ fontSize: "14px", color: "#6b7280" }}>
                      Games Played
                    </div>
                  </div>
                  <div
                    style={{
                      textAlign: "center",
                      padding: "16px",
                      background: theme.cardAfter,
                      borderRadius: "12px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "32px",
                        fontWeight: "bold",
                        color: theme.text,
                      }}
                    >
                      {stats.gamesWon}
                    </div>
                    <div style={{ fontSize: "14px", color: "#6b7280" }}>
                      Games Won
                    </div>
                  </div>
                  <div
                    style={{
                      textAlign: "center",
                      padding: "16px",
                      background: theme.cardBefore,
                      borderRadius: "12px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "32px",
                        fontWeight: "bold",
                        color: theme.text,
                      }}
                    >
                      {stats.currentStreak}
                    </div>
                    <div style={{ fontSize: "14px", color: "#6b7280" }}>
                      Current Streak
                    </div>
                  </div>
                  <div
                    style={{
                      textAlign: "center",
                      padding: "16px",
                      background: theme.cardAfter,
                      borderRadius: "12px",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "32px",
                        fontWeight: "bold",
                        color: theme.text,
                      }}
                    >
                      {stats.maxStreak}
                    </div>
                    <div style={{ fontSize: "14px", color: "#6b7280" }}>
                      Max Streak
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    padding: "16px",
                    background: theme.gameBg,
                    borderRadius: "12px",
                    marginBottom: "16px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: "8px",
                    }}
                  >
                    <span style={{ color: "#6b7280" }}>Best Time:</span>
                    <span style={{ fontWeight: "bold" }}>
                      {stats.bestTime ? formatTime(stats.bestTime) : "--:--"}
                    </span>
                  </div>
                  <div
                    style={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <span style={{ color: "#6b7280" }}>Average Time:</span>
                    <span style={{ fontWeight: "bold" }}>
                      {stats.averageTime
                        ? formatTime(Math.round(stats.averageTime))
                        : "--:--"}
                    </span>
                  </div>
                </div>

                <button
                  onClick={() => setShowStatsModal(false)}
                  style={styles.button}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Profile Modal - Fullscreen */}
      {
        showProfileModal && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: theme.container,
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "center",
              zIndex: 2000,
              padding: "20px",
              overflowY: "auto",
              WebkitOverflowScrolling: "touch",
            }}
          >
            <div
              style={{
                background: theme.gameBg,
                borderRadius: "24px",
                boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
                maxWidth: "500px",
                width: "100%",
                padding: "32px",
                color: theme.text,
                marginTop: "20px",
                marginBottom: "20px",
              }}
            >
              <button
                onClick={() => setShowProfileModal(false)}
                style={{
                  background: "none",
                  border: "none",
                  color: theme.text,
                  cursor: "pointer",
                  fontSize: "16px",
                  marginBottom: "16px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: 0,
                }}
              >
                <ArrowLeft size={20} /> Back
              </button>
              <h2
                style={{
                  fontSize: "36px",
                  fontWeight: "900",
                  color: theme.text,
                  textAlign: "center",
                  marginBottom: "8px",
                }}
              >
                Profile
              </h2>

              <div style={{ marginTop: "24px", textAlign: "center" }}>
                <div
                  style={{
                    marginBottom: "16px",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <div
                    style={{
                      width: "80px",
                      height: "80px",
                      borderRadius: "50%",
                      background: theme.pivotBg,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <User size={40} color="white" />
                  </div>
                </div>
                <div
                  style={{
                    fontSize: "24px",
                    fontWeight: "bold",
                    marginBottom: "8px",
                    color: theme.text,
                  }}
                >
                  {stats.profile.username}
                </div>

                {user && (
                  <button
                    onClick={handleLogout}
                    style={{
                      width: "100%",
                      padding: "12px",
                      background: "#fee2e2",
                      color: "#dc2626",
                      fontWeight: "600",
                      fontSize: "14px",
                      borderRadius: "12px",
                      border: "none",
                      cursor: "pointer",
                      marginTop: "8px",
                      marginBottom: "16px",
                    }}
                  >
                    Sign Out
                  </button>
                )}
                <div
                  style={{
                    color: theme.text === "#ffffff"
                      ? "rgba(255,255,255,0.6)"
                      : "#6b7280",
                    marginBottom: "24px",
                  }}
                >
                  Level {stats.profile.level}
                </div>

                <div
                  style={{
                    padding: "16px",
                    background: theme.text === "#ffffff"
                      ? "rgba(255,255,255,0.1)"
                      : "#f9fafb",
                    borderRadius: "12px",
                    marginBottom: "24px",
                    border: theme.text === "#ffffff"
                      ? "1px solid rgba(255,255,255,0.2)"
                      : "none",
                  }}
                >
                  <div
                    style={{
                      fontSize: "14px",
                      color: theme.text === "#ffffff"
                        ? "rgba(255,255,255,0.6)"
                        : "#6b7280",
                      marginBottom: "8px",
                    }}
                  >
                    XP Progress
                  </div>
                  <div
                    style={{
                      height: "12px",
                      background: theme.text === "#ffffff"
                        ? "rgba(255,255,255,0.2)"
                        : "#e5e7eb",
                      borderRadius: "9999px",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        height: "100%",
                        width: `${stats.profile.xp % 100}%`,
                        background: theme.pivotBg,
                      }}
                    ></div>
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: theme.text === "#ffffff"
                        ? "rgba(255,255,255,0.5)"
                        : "#6b7280",
                      marginTop: "4px",
                    }}
                  >
                    {stats.profile.xp % 100} / 100 XP
                  </div>
                </div>

                <button
                  onClick={() => setShowProfileModal(false)}
                  style={styles.button}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Share Modal - Fullscreen */}
      {
        showShareModal && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: theme.container,
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "center",
              zIndex: 2000,
              padding: "20px",
              overflowY: "auto",
              WebkitOverflowScrolling: "touch",
            }}
          >
            <div
              style={{
                background: theme.gameBg,
                borderRadius: "24px",
                boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
                maxWidth: "500px",
                width: "100%",
                padding: "32px",
                color: theme.text,
                marginTop: "20px",
                marginBottom: "20px",
              }}
            >
              <button
                onClick={() => setShowShareModal(false)}
                style={{
                  background: "none",
                  border: "none",
                  color: theme.text,
                  cursor: "pointer",
                  fontSize: "16px",
                  marginBottom: "16px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: 0,
                }}
              >
                <ArrowLeft size={20} /> Back
              </button>
              <h2
                style={{
                  textAlign: "center",
                  fontSize: "32px",
                  fontWeight: "900",
                  marginBottom: "8px",
                }}
              >
                Share Your Results! <Award size={32} />
              </h2>
              <p
                style={{
                  textAlign: "center",
                  color: "#6b7280",
                  marginBottom: "24px",
                }}
              >
                Show off your Pivot skills
              </p>

              <div
                style={{
                  background: theme.text === "#ffffff"
                    ? "rgba(255,255,255,0.1)"
                    : "#f9fafb",
                  borderRadius: "16px",
                  padding: "16px",
                  marginBottom: "24px",
                  fontFamily: "monospace",
                  fontSize: "14px",
                  whiteSpace: "pre-line",
                  border: theme.text === "#ffffff"
                    ? "2px solid rgba(255,255,255,0.2)"
                    : "2px solid #e5e7eb",
                  color: theme.text,
                }}
              >
                {generateShareText()}
              </div>

              <div
                style={{ display: "flex", flexDirection: "column", gap: "12px" }}
              >
                {/* Native Share API (Primary - shows on mobile/supported browsers) */}
                {navigator.share && (
                  <button
                    onClick={async () => {
                      try {
                        await navigator.share({
                          title: 'Pivot - Daily Word Puzzle',
                          text: generateShareText(),
                        });
                      } catch (err) {
                        // User cancelled or error - do nothing
                      }
                    }}
                    style={{
                      ...styles.button,
                      background: "linear-gradient(135deg, #6366f1, #a855f7, #ec4899)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px",
                    }}
                  >
                    <Share2 size={20} /> Share Results
                  </button>
                )}

                <button
                  onClick={copyShareText}
                  style={{
                    ...styles.button,
                    background: copySuccess ? "#16a34a" : theme.pivotBg,
                  }}
                >
                  {copySuccess ? (
                    <>
                      <Check size={20} /> Copied!
                    </>
                  ) : (
                    <>
                      <Copy size={20} /> Copy to Clipboard
                    </>
                  )}
                </button>

                {/* Social Share Buttons */}
                <div style={{ display: "flex", gap: "8px", justifyContent: "center", flexWrap: "wrap" }}>
                  {/* Twitter/X */}
                  <button
                    onClick={() => {
                      const text = encodeURIComponent(generateShareText());
                      window.open(`https://twitter.com/intent/tweet?text=${text}`, '_blank');
                    }}
                    style={{
                      padding: "12px 16px",
                      background: "#000000",
                      color: "white",
                      fontWeight: "bold",
                      fontSize: "14px",
                      borderRadius: "12px",
                      border: "none",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      transition: "transform 0.2s",
                    }}
                    title="Share on X (Twitter)"
                  >
                    <Twitter size={20} fill="currentColor" />
                  </button>

                  {/* Facebook */}
                  <button
                    onClick={() => {
                      const text = encodeURIComponent(generateShareText());
                      window.open(`https://www.facebook.com/sharer/sharer.php?quote=${text}`, '_blank');
                    }}
                    style={{
                      padding: "12px 16px",
                      background: "#1877F2",
                      color: "white",
                      fontWeight: "bold",
                      fontSize: "14px",
                      borderRadius: "12px",
                      border: "none",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      transition: "transform 0.2s",
                    }}
                    title="Share on Facebook"
                  >
                    <Facebook size={20} fill="currentColor" />
                  </button>

                  {/* WhatsApp */}
                  <button
                    onClick={() => {
                      const text = encodeURIComponent(generateShareText());
                      window.open(`https://wa.me/?text=${text}`, '_blank');
                    }}
                    style={{
                      padding: "12px 16px",
                      background: "#25D366",
                      color: "white",
                      fontWeight: "bold",
                      fontSize: "14px",
                      borderRadius: "12px",
                      border: "none",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      transition: "transform 0.2s",
                    }}
                    title="Share on WhatsApp"
                  >
                    <MessageCircle size={20} fill="currentColor" />
                  </button>

                  {/* LinkedIn */}
                  <button
                    onClick={() => {
                      const text = encodeURIComponent(generateShareText());
                      window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}&summary=${text}`, '_blank');
                    }}
                    style={{
                      padding: "12px 16px",
                      background: "#0A66C2",
                      color: "white",
                      fontWeight: "bold",
                      fontSize: "14px",
                      borderRadius: "12px",
                      border: "none",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      transition: "transform 0.2s",
                    }}
                    title="Share on LinkedIn"
                  >
                    in
                  </button>

                  {/* Reddit */}
                  <button
                    onClick={() => {
                      const text = encodeURIComponent(generateShareText());
                      const title = encodeURIComponent(`Pivot #${gameData.puzzleNumber} - Daily Word Puzzle`);
                      window.open(`https://www.reddit.com/submit?title=${title}&text=${text}`, '_blank');
                    }}
                    style={{
                      padding: "12px 16px",
                      background: "#FF4500",
                      color: "white",
                      fontWeight: "bold",
                      fontSize: "14px",
                      borderRadius: "12px",
                      border: "none",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      transition: "transform 0.2s",
                    }}
                    title="Share on Reddit"
                  >
                    ↗
                  </button>

                  {/* SMS/Text */}
                  <button
                    onClick={() => {
                      const text = encodeURIComponent(generateShareText());
                      // Use different format for iOS vs Android
                      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
                      const smsUrl = isIOS ? `sms:&body=${text}` : `sms:?body=${text}`;
                      window.open(smsUrl, '_self');
                    }}
                    style={{
                      padding: "12px 16px",
                      background: "#34C759",
                      color: "white",
                      fontWeight: "bold",
                      fontSize: "14px",
                      borderRadius: "12px",
                      border: "none",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      transition: "transform 0.2s",
                    }}
                    title="Share via Text Message"
                  >
                    SMS
                  </button>
                </div>

                <button
                  onClick={() => setShowShareModal(false)}
                  style={{
                    ...styles.button,
                    background: "#e5e7eb",
                    color: "#374151",
                    marginTop: "8px",
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Terms of Service Modal */}
      {
        showTerms && (
          <div style={styles.modal} onClick={() => setShowTerms(false)}>
            <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
              <h2
                style={{
                  fontSize: "28px",
                  fontWeight: "900",
                  color: getTitleColor(),
                  textAlign: "center",
                  marginBottom: "16px",
                }}
              >
                Terms of Service
              </h2>

              <div
                style={{
                  maxHeight: "60vh",
                  overflowY: "auto",
                  fontSize: "14px",
                  lineHeight: "1.6",
                  color: theme.text,
                  marginBottom: "24px",
                }}
              >
                <p style={{ marginBottom: "16px" }}>
                  <strong>Last Updated:</strong> December 7, 2025
                </p>

                <h3
                  style={{
                    fontSize: "16px",
                    fontWeight: "bold",
                    marginTop: "16px",
                    marginBottom: "8px",
                  }}
                >
                  1. Acceptance of Terms
                </h3>
                <p style={{ marginBottom: "12px" }}>
                  By accessing and playing this game ("Pivot"), you agree to be
                  bound by these Terms of Service. If you do not agree to these
                  terms, please do not use this game.
                </p>

                <h3
                  style={{
                    fontSize: "16px",
                    fontWeight: "bold",
                    marginTop: "16px",
                    marginBottom: "8px",
                  }}
                >
                  2. Intellectual Property Rights
                </h3>
                <p style={{ marginBottom: "12px" }}>
                  This game, including all code, design, graphics, text, puzzles,
                  and functionality, is the proprietary property of Jorge
                  Contreras Mendez and is protected by United States and
                  international copyright laws.
                </p>

                <h3
                  style={{
                    fontSize: "16px",
                    fontWeight: "bold",
                    marginTop: "16px",
                    marginBottom: "8px",
                  }}
                >
                  3. Prohibited Uses
                </h3>
                <p style={{ marginBottom: "8px" }}>You agree NOT to:</p>
                <ul style={{ marginLeft: "20px", marginBottom: "12px" }}>
                  <li>
                    Copy, reproduce, or distribute the game or any portion thereof
                  </li>
                  <li>
                    Reverse engineer, decompile, or disassemble the game code
                  </li>
                  <li>Create derivative works based on this game</li>
                  <li>
                    Use the game for any commercial purpose without written
                    permission
                  </li>
                  <li>
                    Remove or alter any copyright notices or proprietary markings
                  </li>
                  <li>Use automated tools or bots to play the game</li>
                </ul>

                <h3
                  style={{
                    fontSize: "16px",
                    fontWeight: "bold",
                    marginTop: "16px",
                    marginBottom: "8px",
                  }}
                >
                  4. User Data and Privacy
                </h3>
                <p style={{ marginBottom: "12px" }}>
                  Game statistics and progress are stored locally on your device
                  using browser localStorage. We do not collect, transmit, or
                  store any personal information on external servers.
                </p>

                <h3
                  style={{
                    fontSize: "16px",
                    fontWeight: "bold",
                    marginTop: "16px",
                    marginBottom: "8px",
                  }}
                >
                  5. Disclaimer of Warranties
                </h3>
                <p style={{ marginBottom: "12px" }}>
                  This game is provided "AS IS" without any warranties, express or
                  implied. We do not guarantee that the game will be error-free or
                  uninterrupted.
                </p>

                <h3
                  style={{
                    fontSize: "16px",
                    fontWeight: "bold",
                    marginTop: "16px",
                    marginBottom: "8px",
                  }}
                >
                  6. Limitation of Liability
                </h3>
                <p style={{ marginBottom: "12px" }}>
                  Jorge Contreras Mendez shall not be liable for any indirect,
                  incidental, special, consequential, or punitive damages
                  resulting from your use or inability to use this game.
                </p>

                <h3
                  style={{
                    fontSize: "16px",
                    fontWeight: "bold",
                    marginTop: "16px",
                    marginBottom: "8px",
                  }}
                >
                  7. Changes to Terms
                </h3>
                <p style={{ marginBottom: "12px" }}>
                  We reserve the right to modify these terms at any time.
                  Continued use of the game after changes constitutes acceptance
                  of the modified terms.
                </p>

                <h3
                  style={{
                    fontSize: "16px",
                    fontWeight: "bold",
                    marginTop: "16px",
                    marginBottom: "8px",
                  }}
                >
                  8. Contact Information
                </h3>
                <p style={{ marginBottom: "12px" }}>
                  For licensing inquiries or questions about these terms, please
                  contact: [Your Email]
                </p>

                <p
                  style={{
                    marginTop: "24px",
                    fontSize: "12px",
                    color: "#6b7280",
                  }}
                >
                  © 2025 Jorge Contreras Mendez. All Rights Reserved.
                </p>
              </div>

              <button onClick={() => setShowTerms(false)} style={styles.button}>
                I Understand
              </button>
            </div>
          </div>
        )
      }

      {/* Help & Support Modal - Fullscreen */}
      {
        showHelp && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: theme.container,
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "center",
              zIndex: 2000,
              padding: "20px",
              overflowY: "auto",
              WebkitOverflowScrolling: "touch",
            }}
          >
            <div
              style={{
                background: theme.gameBg,
                borderRadius: "24px",
                boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
                maxWidth: "500px",
                width: "100%",
                padding: "32px",
                color: theme.text,
                marginTop: "20px",
                marginBottom: "20px",
              }}
            >
              <button
                onClick={() => setShowHelp(false)}
                style={{
                  background: "none",
                  border: "none",
                  color: theme.text,
                  cursor: "pointer",
                  fontSize: "16px",
                  marginBottom: "16px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: 0,
                }}
              >
                <ArrowLeft size={20} /> Back
              </button>
              {helpSection === "menu" && (
                <>
                  <h2
                    style={{
                      fontSize: "32px",
                      fontWeight: "900",
                      color: getTitleColor(),
                      textAlign: "center",
                      marginBottom: "24px",
                    }}
                  >
                    <HelpCircle size={32} /> Help & Support
                  </h2>

                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "12px",
                      marginBottom: "24px",
                    }}
                  >
                    <button
                      onClick={() => setHelpSection("bug")}
                      style={{
                        ...styles.button,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        textAlign: "left",
                      }}
                    >
                      <span>
                        <Bug size={20} /> Report a Bug
                      </span>
                      <ChevronRight size={16} />
                    </button>

                    <button
                      onClick={() => setHelpSection("feedback")}
                      style={{
                        ...styles.button,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        textAlign: "left",
                      }}
                    >
                      <span>
                        <MessageSquare size={20} /> Send Feedback
                      </span>
                      <ChevronRight size={16} />
                    </button>

                    <button
                      onClick={() => setHelpSection("faq")}
                      style={{
                        ...styles.button,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        textAlign: "left",
                      }}
                    >
                      <span>
                        <HelpCircle size={20} /> Frequently Asked Questions
                      </span>
                      <ChevronRight size={16} />
                    </button>

                    <button
                      onClick={() => setHelpSection("glossary")}
                      style={{
                        ...styles.button,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        textAlign: "left",
                      }}
                    >
                      <span>
                        <Book size={20} /> Glossary
                      </span>
                      <ChevronRight size={16} />
                    </button>
                  </div>

                  <button
                    onClick={() => setShowHelp(false)}
                    style={{
                      ...styles.button,
                      background: "#e5e7eb",
                      color: "#374151",
                    }}
                  >
                    Close
                  </button>
                </>
              )}

              {helpSection === "bug" && (
                <>
                  <button
                    onClick={() => setHelpSection("menu")}
                    style={{
                      background: "none",
                      border: "none",
                      color: theme.text,
                      cursor: "pointer",
                      fontSize: "16px",
                      marginBottom: "16px",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <ArrowLeft size={20} /> Back
                  </button>

                  <h2
                    style={{
                      fontSize: "28px",
                      fontWeight: "900",
                      color: getTitleColor(),
                      textAlign: "center",
                      marginBottom: "16px",
                    }}
                  >
                    <Bug size={28} /> Report a Bug
                  </h2>

                  <div
                    style={{
                      fontSize: "14px",
                      lineHeight: "1.6",
                      color: theme.text,
                      marginBottom: "24px",
                    }}
                  >
                    <p style={{ marginBottom: "16px" }}>
                      Found a bug? Help us improve Pivot by reporting it!
                    </p>

                    <h3
                      style={{
                        fontSize: "16px",
                        fontWeight: "bold",
                        marginBottom: "12px",
                      }}
                    >
                      What to Include:
                    </h3>
                    <ul style={{ marginLeft: "20px", marginBottom: "16px" }}>
                      <li>What were you doing when the bug occurred?</li>
                      <li>What did you expect to happen?</li>
                      <li>What actually happened?</li>
                      <li>Your browser and device type</li>
                      <li>Screenshot (if possible)</li>
                    </ul>

                    <div
                      style={{
                        background: theme.gameBg,
                        border: `2px solid ${theme.text}20`,
                        borderRadius: "12px",
                        padding: "16px",
                        marginBottom: "16px",
                      }}
                    >
                      <p style={{ fontWeight: "600", marginBottom: "8px" }}>
                        <Mail size={16} /> Email Bug Report To:
                      </p>
                      <p
                        style={{
                          fontFamily: "monospace",
                          fontSize: "14px",
                          color: getTitleColor(),
                          wordBreak: "break-all",
                        }}
                      >
                        pivot.game.bugs@gmail.com
                      </p>
                    </div>

                    <p
                      style={{
                        fontSize: "12px",
                        color: theme.text + "80",
                        fontStyle: "italic",
                      }}
                    >
                      We read every report and appreciate your help in making
                      Pivot better!
                    </p>
                  </div>

                  <button
                    onClick={() => setHelpSection("menu")}
                    style={styles.button}
                  >
                    Back to Menu
                  </button>
                </>
              )}

              {helpSection === "feedback" && (
                <>
                  <button
                    onClick={() => setHelpSection("menu")}
                    style={{
                      background: "none",
                      border: "none",
                      color: theme.text,
                      cursor: "pointer",
                      fontSize: "16px",
                      marginBottom: "16px",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <ArrowLeft size={20} /> Back
                  </button>

                  <h2
                    style={{
                      fontSize: "28px",
                      fontWeight: "900",
                      color: getTitleColor(),
                      textAlign: "center",
                      marginBottom: "16px",
                    }}
                  >
                    <MessageSquare size={28} /> Send Feedback
                  </h2>

                  <div
                    style={{
                      fontSize: "14px",
                      lineHeight: "1.6",
                      color: theme.text,
                      marginBottom: "24px",
                    }}
                  >
                    <p style={{ marginBottom: "16px" }}>
                      We'd love to hear your thoughts! Your feedback helps shape
                      the future of Pivot.
                    </p>

                    <h3
                      style={{
                        fontSize: "16px",
                        fontWeight: "bold",
                        marginBottom: "12px",
                      }}
                    >
                      We Want to Know:
                    </h3>
                    <ul style={{ marginLeft: "20px", marginBottom: "16px" }}>
                      <li>What do you love about Pivot?</li>
                      <li>What features would you like to see?</li>
                      <li>What could be improved?</li>
                      <li>How's the difficulty level?</li>
                      <li>Any puzzle suggestions?</li>
                    </ul>

                    <div
                      style={{
                        background: theme.gameBg,
                        border: `2px solid ${theme.text}20`,
                        borderRadius: "12px",
                        padding: "16px",
                        marginBottom: "16px",
                      }}
                    >
                      <p style={{ fontWeight: "600", marginBottom: "8px" }}>
                        <Mail size={16} /> Send Feedback To:
                      </p>
                      <p
                        style={{
                          fontFamily: "monospace",
                          fontSize: "14px",
                          color: getTitleColor(),
                          wordBreak: "break-all",
                        }}
                      >
                        pivot.game.feedback@gmail.com
                      </p>
                    </div>

                    <p
                      style={{
                        fontSize: "12px",
                        color: theme.text + "80",
                        fontStyle: "italic",
                      }}
                    >
                      Every piece of feedback is read and considered. Thank you
                      for helping us improve!
                    </p>
                  </div>

                  <button
                    onClick={() => setHelpSection("menu")}
                    style={styles.button}
                  >
                    Back to Menu
                  </button>
                </>
              )}

              {helpSection === "faq" && (
                <>
                  <button
                    onClick={() => setHelpSection("menu")}
                    style={{
                      background: "none",
                      border: "none",
                      color: theme.text,
                      cursor: "pointer",
                      fontSize: "16px",
                      marginBottom: "16px",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <ArrowLeft size={20} /> Back
                  </button>

                  <h2
                    style={{
                      fontSize: "28px",
                      fontWeight: "900",
                      color: getTitleColor(),
                      textAlign: "center",
                      marginBottom: "16px",
                    }}
                  >
                    <HelpCircle size={28} /> Frequently Asked Questions
                  </h2>

                  <div
                    style={{
                      fontSize: "14px",
                      lineHeight: "1.6",
                      color: theme.text,
                      marginBottom: "24px",
                      maxHeight: "60vh",
                      overflowY: "auto",
                    }}
                  >
                    <div style={{ marginBottom: "20px" }}>
                      <h3 style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "8px" }}>
                        How do I play Pivot?
                      </h3>
                      <p style={{ marginBottom: "12px" }}>
                        Find words that make valid phrases before OR after the pivot word. Type your word in the input field and tap the arrow button (or press Enter) to submit. Green arrow for "before" words, purple arrow for "after" words.
                      </p>
                    </div>

                    <div style={{ marginBottom: "20px" }}>
                      <h3 style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "8px" }}>
                        What are the different game modes?
                      </h3>
                      <p style={{ marginBottom: "8px" }}>
                        There are three modes (only one can be active at a time):
                      </p>
                      <ul style={{ marginLeft: "20px", marginBottom: "12px" }}>
                        <li><strong>Standard:</strong> Timer tracks your speed, 5 wrong guesses allowed, hints available</li>
                        <li><strong>Casual:</strong> No timer, unlimited wrong guesses, hints available - perfect for relaxed play</li>
                        <li><strong>Hard:</strong> Timer + 5 wrong guesses, but NO hints and word lists are hidden</li>
                      </ul>
                    </div>

                    <div style={{ marginBottom: "20px" }}>
                      <h3 style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "8px" }}>
                        What happens when I make a wrong guess?
                      </h3>
                      <p style={{ marginBottom: "12px" }}>
                        In Standard and Hard modes, you have 5 wrong guesses before the game ends. The dots below the puzzle info show your remaining guesses. In Casual mode, wrong guesses don't count against you.
                      </p>
                    </div>

                    <div style={{ marginBottom: "20px" }}>
                      <h3 style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "8px" }}>
                        When does a new puzzle come out?
                      </h3>
                      <p style={{ marginBottom: "12px" }}>
                        A new daily puzzle is available every day at midnight in your local timezone. Puzzle #1 started on January 1, 2025.
                      </p>
                    </div>

                    <div style={{ marginBottom: "20px" }}>
                      <h3 style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "8px" }}>
                        How do hints work?
                      </h3>
                      <p style={{ marginBottom: "12px" }}>
                        Each puzzle has up to 3 hints that give you clues about the types of words to look for. Hints are available in Standard and Casual modes, but NOT in Hard mode.
                      </p>
                    </div>

                    <div style={{ marginBottom: "20px" }}>
                      <h3 style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "8px" }}>
                        What is the onscreen keyboard?
                      </h3>
                      <p style={{ marginBottom: "12px" }}>
                        An optional virtual keyboard you can enable in Settings. It's useful on mobile devices where the native keyboard might cover the game. Toggle it on/off anytime in Settings.
                      </p>
                    </div>

                    <div style={{ marginBottom: "20px" }}>
                      <h3 style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "8px" }}>
                        Are my stats saved?
                      </h3>
                      <p style={{ marginBottom: "12px" }}>
                        Yes! Your stats are saved locally in your browser. As long as you don't clear your browser data, they'll persist. Stats include games played, wins, streaks, and best times.
                      </p>
                    </div>

                    <div style={{ marginBottom: "20px" }}>
                      <h3 style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "8px" }}>
                        Can I play old puzzles?
                      </h3>
                      <p style={{ marginBottom: "12px" }}>
                        This feature is coming soon! We're working on a puzzle archive where you can replay past puzzles.
                      </p>
                    </div>

                    <div style={{ marginBottom: "20px" }}>
                      <h3 style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "8px" }}>
                        What are the difficulty levels shown on puzzles?
                      </h3>
                      <p style={{ marginBottom: "12px" }}>
                        Each daily puzzle has a difficulty rating (Easy, Medium, Hard) based on how common the words are. This is separate from the game mode you choose.
                      </p>
                    </div>

                    <div style={{ marginBottom: "20px" }}>
                      <h3 style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "8px" }}>
                        Can I share my results?
                      </h3>
                      <p style={{ marginBottom: "12px" }}>
                        Yes! After completing a puzzle (or when game ends), tap the share button to copy your results with emoji grids showing your progress. Share on social media or with friends!
                      </p>
                    </div>

                    <div style={{ marginBottom: "20px" }}>
                      <h3 style={{ fontSize: "16px", fontWeight: "bold", marginBottom: "8px" }}>
                        What do the ranks mean?
                      </h3>
                      <p style={{ marginBottom: "12px" }}>
                        Your rank is based on completion percentage: Getting Started (under 30%), Good (30%+), Great (50%+), Amazing (75%+), and Genius (100% - all words found).
                      </p>
                    </div>
                  </div>

                  <button
                    onClick={() => setHelpSection("menu")}
                    style={styles.button}
                  >
                    Back to Menu
                  </button>
                </>
              )}



              {helpSection === "glossary" && (
                <>
                  <button
                    onClick={() => setHelpSection("menu")}
                    style={{
                      background: "none",
                      border: "none",
                      color: theme.text,
                      cursor: "pointer",
                      fontSize: "16px",
                      marginBottom: "16px",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <ArrowLeft size={20} /> Back
                  </button>

                  <h2
                    style={{
                      fontSize: "28px",
                      fontWeight: "900",
                      color: getTitleColor(),
                      textAlign: "center",
                      marginBottom: "16px",
                    }}
                  >
                    <Book size={28} /> Glossary
                  </h2>

                  <div
                    style={{
                      fontSize: "14px",
                      lineHeight: "1.6",
                      color: theme.text,
                      marginBottom: "24px",
                      maxHeight: "60vh",
                      overflowY: "auto",
                    }}
                  >
                    <div style={{ marginBottom: "16px" }}>
                      <h3 style={{ fontSize: "16px", fontWeight: "bold", color: getTitleColor() }}>
                        Pivot Word
                      </h3>
                      <p style={{ marginBottom: "12px" }}>
                        The central word displayed in the middle of the game. All valid answers connect either before or after this word to form common phrases or compound words.
                      </p>
                    </div>

                    <div style={{ marginBottom: "16px" }}>
                      <h3 style={{ fontSize: "16px", fontWeight: "bold", color: getTitleColor() }}>
                        Before Words
                      </h3>
                      <p style={{ marginBottom: "12px" }}>
                        Words that come before the pivot word to make a valid phrase. Shown with a green arrow (<ArrowUp size={16} style={{ display: "inline", verticalAlign: "middle" }} />). Example: "lunch" + "break" = "lunch break".
                      </p>
                    </div>

                    <div style={{ marginBottom: "16px" }}>
                      <h3 style={{ fontSize: "16px", fontWeight: "bold", color: getTitleColor() }}>
                        After Words
                      </h3>
                      <p style={{ marginBottom: "12px" }}>
                        Words that come after the pivot word to make a valid phrase. Shown with a purple arrow (<ArrowDown size={16} style={{ display: "inline", verticalAlign: "middle" }} />). Example: "break" + "dance" = "break dance".
                      </p>
                    </div>

                    <div style={{ marginBottom: "16px" }}>
                      <h3 style={{ fontSize: "16px", fontWeight: "bold", color: getTitleColor() }}>
                        Standard Mode
                      </h3>
                      <p style={{ marginBottom: "12px" }}>
                        The default game mode. Features a timer that tracks your completion speed, a limit of 5 wrong guesses before game over, and access to hints when stuck.
                      </p>
                    </div>

                    <div style={{ marginBottom: "16px" }}>
                      <h3 style={{ fontSize: "16px", fontWeight: "bold", color: getTitleColor() }}>
                        Casual Mode
                      </h3>
                      <p style={{ marginBottom: "12px" }}>
                        A relaxed mode with no timer and unlimited wrong guesses. Hints are still available. Perfect for learning or stress-free play.
                      </p>
                    </div>

                    <div style={{ marginBottom: "16px" }}>
                      <h3 style={{ fontSize: "16px", fontWeight: "bold", color: getTitleColor() }}>
                        Hard Mode
                      </h3>
                      <p style={{ marginBottom: "12px" }}>
                        The most challenging mode. Like Standard, it has a timer and 5 wrong guess limit, but hints are disabled and word lists only show counts (not the actual words found).
                      </p>
                    </div>

                    <div style={{ marginBottom: "16px" }}>
                      <h3 style={{ fontSize: "16px", fontWeight: "bold", color: getTitleColor() }}>
                        Wrong Guesses
                      </h3>
                      <p style={{ marginBottom: "12px" }}>
                        In Standard and Hard modes, you can only make 5 incorrect guesses before the game ends. Shown as dots below the puzzle info - red dots indicate used guesses.
                      </p>
                    </div>

                    <div style={{ marginBottom: "16px" }}>
                      <h3 style={{ fontSize: "16px", fontWeight: "bold", color: getTitleColor() }}>
                        Game Over
                      </h3>
                      <p style={{ marginBottom: "12px" }}>
                        Occurs in Standard or Hard mode when you've used all 5 wrong guesses. You can still view the puzzle and share your results, but can't submit more words.
                      </p>
                    </div>

                    <div style={{ marginBottom: "16px" }}>
                      <h3 style={{ fontSize: "16px", fontWeight: "bold", color: getTitleColor() }}>
                        Compound Word
                      </h3>
                      <p style={{ marginBottom: "12px" }}>
                        Two words combined to create a new word or phrase. Examples: "sunlight", "notebook", "ice cream", "break dance".
                      </p>
                    </div>

                    <div style={{ marginBottom: "16px" }}>
                      <h3 style={{ fontSize: "16px", fontWeight: "bold", color: getTitleColor() }}>
                        Streak
                      </h3>
                      <p style={{ marginBottom: "12px" }}>
                        The number of consecutive days you've completed puzzles (found all words). Missing a day or getting a game over resets your streak.
                      </p>
                    </div>

                    <div style={{ marginBottom: "16px" }}>
                      <h3 style={{ fontSize: "16px", fontWeight: "bold", color: getTitleColor() }}>
                        Rank
                      </h3>
                      <p style={{ marginBottom: "12px" }}>
                        Your performance rating based on completion percentage: Getting Started (0-29%), Good (30-49%), Great (50-74%), Amazing (75-99%), Genius (100%).
                      </p>
                    </div>

                    <div style={{ marginBottom: "16px" }}>
                      <h3 style={{ fontSize: "16px", fontWeight: "bold", color: getTitleColor() }}>
                        Puzzle Number
                      </h3>
                      <p style={{ marginBottom: "12px" }}>
                        Each daily puzzle has a unique number starting from #1 on January 1, 2025. The number increments by 1 each day.
                      </p>
                    </div>

                    <div style={{ marginBottom: "16px" }}>
                      <h3 style={{ fontSize: "16px", fontWeight: "bold", color: getTitleColor() }}>
                        Onscreen Keyboard
                      </h3>
                      <p style={{ marginBottom: "12px" }}>
                        An optional virtual keyboard that appears below the game. Enable it in Settings if your device's keyboard covers the game area.
                      </p>
                    </div>

                    <div style={{ marginBottom: "16px" }}>
                      <h3 style={{ fontSize: "16px", fontWeight: "bold", color: getTitleColor() }}>
                        High Contrast Mode
                      </h3>
                      <p style={{ marginBottom: "12px" }}>
                        An accessibility setting that uses solid colors instead of gradients for better visibility. Found in Settings under Appearance.
                      </p>
                    </div>
                  </div>

                  <button
                    onClick={() => setHelpSection("menu")}
                    style={styles.button}
                  >
                    Back to Menu
                  </button>
                </>
              )}

            </div >
          </div >
        )
      }

      {/* Terms of Service Modal */}
      {
        showTerms && (
          <div style={styles.modal} onClick={() => setShowTerms(false)}>
            <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
              <h2
                style={{
                  fontSize: "28px",
                  fontWeight: "900",
                  color: getTitleColor(),
                  textAlign: "center",
                  marginBottom: "16px",
                }}
              >
                Terms of Service
              </h2>

              <div
                style={{
                  maxHeight: "60vh",
                  overflowY: "auto",
                  fontSize: "14px",
                  lineHeight: "1.6",
                  color: theme.text,
                  marginBottom: "24px",
                }}
              >
                <p style={{ marginBottom: "16px" }}>
                  <strong>Last Updated:</strong> December 7, 2025
                </p>

                <h3
                  style={{
                    fontSize: "16px",
                    fontWeight: "bold",
                    marginTop: "16px",
                    marginBottom: "8px",
                  }}
                >
                  1. Acceptance of Terms
                </h3>
                <p style={{ marginBottom: "12px" }}>
                  By accessing and playing this game ("Pivot"), you agree to be
                  bound by these Terms of Service. If you do not agree to these
                  terms, please do not use this game.
                </p>

                <h3
                  style={{
                    fontSize: "16px",
                    fontWeight: "bold",
                    marginTop: "16px",
                    marginBottom: "8px",
                  }}
                >
                  2. Intellectual Property Rights
                </h3>
                <p style={{ marginBottom: "12px" }}>
                  This game, including all code, design, graphics, text, puzzles,
                  and functionality, is the proprietary property of Jorge
                  Contreras Mendez and is protected by United States and
                  international copyright laws.
                </p>

                <h3
                  style={{
                    fontSize: "16px",
                    fontWeight: "bold",
                    marginTop: "16px",
                    marginBottom: "8px",
                  }}
                >
                  3. Prohibited Uses
                </h3>
                <p style={{ marginBottom: "8px" }}>You agree NOT to:</p>
                <ul style={{ marginLeft: "20px", marginBottom: "12px" }}>
                  <li>
                    Copy, reproduce, or distribute the game or any portion thereof
                  </li>
                  <li>
                    Reverse engineer, decompile, or disassemble the game code
                  </li>
                  <li>Create derivative works based on this game</li>
                  <li>
                    Use the game for any commercial purpose without written
                    permission
                  </li>
                  <li>
                    Remove or alter any copyright notices or proprietary markings
                  </li>
                  <li>Use automated tools or bots to play the game</li>
                </ul>

                <h3
                  style={{
                    fontSize: "16px",
                    fontWeight: "bold",
                    marginTop: "16px",
                    marginBottom: "8px",
                  }}
                >
                  4. User Data and Privacy
                </h3>
                <p style={{ marginBottom: "12px" }}>
                  Game statistics and progress are stored locally on your device
                  using browser localStorage. We do not collect, transmit, or
                  store any personal information on external servers.
                </p>

                <h3
                  style={{
                    fontSize: "16px",
                    fontWeight: "bold",
                    marginTop: "16px",
                    marginBottom: "8px",
                  }}
                >
                  5. Disclaimer of Warranties
                </h3>
                <p style={{ marginBottom: "12px" }}>
                  This game is provided "AS IS" without any warranties, express or
                  implied. We do not guarantee that the game will be error-free or
                  uninterrupted.
                </p>

                <h3
                  style={{
                    fontSize: "16px",
                    fontWeight: "bold",
                    marginTop: "16px",
                    marginBottom: "8px",
                  }}
                >
                  6. Limitation of Liability
                </h3>
                <p style={{ marginBottom: "12px" }}>
                  Jorge Contreras Mendez shall not be liable for any indirect,
                  incidental, special, consequential, or punitive damages
                  resulting from your use or inability to use this game.
                </p>

                <h3
                  style={{
                    fontSize: "16px",
                    fontWeight: "bold",
                    marginTop: "16px",
                    marginBottom: "8px",
                  }}
                >
                  7. Changes to Terms
                </h3>
                <p style={{ marginBottom: "12px" }}>
                  We reserve the right to modify these terms at any time.
                  Continued use of the game after changes constitutes acceptance
                  of the modified terms.
                </p>

                <h3
                  style={{
                    fontSize: "16px",
                    fontWeight: "bold",
                    marginTop: "16px",
                    marginBottom: "8px",
                  }}
                >
                  8. Contact Information
                </h3>
                <p style={{ marginBottom: "12px" }}>
                  For licensing inquiries or questions about these terms, please
                  contact: [Your Email]
                </p>

                <p
                  style={{
                    marginTop: "24px",
                    fontSize: "12px",
                    color: "#6b7280",
                  }}
                >
                  © 2025 Jorge Contreras Mendez. All Rights Reserved.
                </p>
              </div>

              <button onClick={() => setShowTerms(false)} style={styles.button}>
                I Understand
              </button>
            </div>
          </div>
        )
      }

      {/* Copyright Notice Modal */}
      {
        showCopyright && (
          <div style={styles.modal} onClick={() => setShowCopyright(false)}>
            <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
              <h2
                style={{
                  fontSize: "28px",
                  fontWeight: "900",
                  color: getTitleColor(),
                  textAlign: "center",
                  marginBottom: "16px",
                }}
              >
                Copyright Notice
              </h2>

              <div
                style={{
                  fontSize: "14px",
                  lineHeight: "1.8",
                  color: theme.text,
                  marginBottom: "24px",
                }}
              >
                <div
                  style={{
                    background:
                      theme.text === "#ffffff"
                        ? "rgba(255,255,255,0.1)"
                        : "#f9fafb",
                    border:
                      "2px solid " +
                      (theme.text === "#ffffff"
                        ? "rgba(255,255,255,0.2)"
                        : "#e5e7eb"),
                    borderRadius: "12px",
                    padding: "20px",
                    marginBottom: "20px",
                  }}
                >
                  <p
                    style={{
                      fontSize: "16px",
                      fontWeight: "bold",
                      marginBottom: "12px",
                      textAlign: "center",
                    }}
                  >
                    Copyright © 2025 Jorge Contreras Mendez
                  </p>
                  <p
                    style={{
                      fontSize: "16px",
                      fontWeight: "bold",
                      marginBottom: "12px",
                      textAlign: "center",
                    }}
                  >
                    All Rights Reserved
                  </p>
                </div>

                <p style={{ marginBottom: "16px" }}>
                  This word game, including but not limited to its design, code,
                  graphics, user interface, puzzle content, and overall
                  presentation, is protected by United States and international
                  copyright laws.
                </p>

                <h3
                  style={{
                    fontSize: "16px",
                    fontWeight: "bold",
                    marginTop: "20px",
                    marginBottom: "12px",
                  }}
                >
                  Protected Elements Include:
                </h3>
                <ul style={{ marginLeft: "20px", marginBottom: "16px" }}>
                  <li>Original source code and software architecture</li>
                  <li>Game mechanics and puzzle design</li>
                  <li>User interface and visual design elements</li>
                  <li>All puzzle content and word combinations</li>
                  <li>Theme designs and color schemes</li>
                  <li>Sound effects and animations</li>
                </ul>

                <h3
                  style={{
                    fontSize: "16px",
                    fontWeight: "bold",
                    marginTop: "20px",
                    marginBottom: "12px",
                  }}
                >
                  Unauthorized Use Prohibited:
                </h3>
                <p style={{ marginBottom: "12px" }}>
                  No part of this game may be reproduced, distributed,
                  transmitted, displayed, published, or broadcast without prior
                  written permission from Jorge Contreras Mendez, except as
                  permitted by U.S. copyright law.
                </p>

                <h3
                  style={{
                    fontSize: "16px",
                    fontWeight: "bold",
                    marginTop: "20px",
                    marginBottom: "12px",
                  }}
                >
                  Fair Use Notice:
                </h3>
                <p style={{ marginBottom: "12px" }}>
                  Playing the game for personal entertainment is permitted.
                  Sharing your personal game results (via the share feature) is
                  encouraged. All other uses require explicit permission.
                </p>

                <h3
                  style={{
                    fontSize: "16px",
                    fontWeight: "bold",
                    marginTop: "20px",
                    marginBottom: "12px",
                  }}
                >
                  DMCA Compliance:
                </h3>
                <p style={{ marginBottom: "12px" }}>
                  This work is protected under the Digital Millennium Copyright
                  Act (DMCA). Unauthorized copying, distribution, or creation of
                  derivative works will be pursued to the fullest extent of the
                  law.
                </p>

                <h3
                  style={{
                    fontSize: "16px",
                    fontWeight: "bold",
                    marginTop: "20px",
                    marginBottom: "12px",
                  }}
                >
                  Licensing Inquiries:
                </h3>
                <p style={{ marginBottom: "12px" }}>
                  For licensing, partnership, or permission requests, please
                  contact: [Your Email]
                </p>

                <p
                  style={{
                    marginTop: "24px",
                    fontSize: "12px",
                    color: "#6b7280",
                    fontStyle: "italic",
                  }}
                >
                  This copyright notice must remain intact in all copies or
                  substantial portions of the software.
                </p>
              </div>

              <button
                onClick={() => setShowCopyright(false)}
                style={styles.button}
              >
                Close
              </button>
            </div>
          </div>
        )
      }

      {/* Celebration Particles */}
      {
        celebrationParticles.map((particle) => (
          <div
            key={particle.id}
            style={{
              ...styles.particle,
              left: `${particle.left}%`,
              animationDelay: `${particle.delay}s`,
            }}
          >
            <Sparkles size={24} color="#fbbf24" fill="#fbbf24" />
          </div>
        ))
      }

      <div
        style={{
          maxWidth: "1000px",
          margin: "0 auto",
          paddingTop: "32px",
          paddingBottom: "32px",
        }}
      >
        {/* Header with Theme & Profile Buttons */}
        <div style={{ textAlign: "center", marginBottom: "32px" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "16px",
              padding: "0 20px",
            }}
          >
            <button
              onClick={() => setShowSettingsModal(true)}
              style={styles.themeButton}
              title="Settings"
            >
              <Settings size={24} />
            </button>

            <div style={{ flex: 1 }}></div>

            <button
              onClick={() => setShowStatsModal(true)}
              style={styles.themeButton}
              title="View statistics"
            >
              <BarChart2 size={24} />
            </button>

            <button
              onClick={() => {
                if (foundWords >= totalWords) {
                  setShowVictoryModal(true);
                } else {
                  setShowShareModal(true);
                }
              }}
              style={{ ...styles.themeButton, marginLeft: "8px" }}
              title="Share"
            >
              <Share2 size={24} />
            </button>

            <button
              onClick={() => {
                setShowHelp(true);
                setHelpSection("menu");
              }}
              style={{ ...styles.themeButton, marginLeft: "8px" }}
              title="Help & Support"
            >
              <HelpCircle size={24} />
            </button>

            <button
              onClick={() => {
                if (user) {
                  setShowProfileModal(true);
                } else {
                  setShowLoginModal(true);
                }
              }}
              style={{ ...styles.themeButton, marginLeft: "8px" }}
              title={user ? "Your Account" : "Log In"}
            >
              {user ? <CheckCircle size={24} color="#22c55e" /> : <User size={24} />}
            </button>
          </div>

          <h1
            style={{
              fontSize: "36px",
              fontWeight: "900",
              color: getTitleColor(),
              textAlign: "center",
              marginBottom: "8px",
            }}
          >
            Pivot
          </h1>

          {/* BETA Badge */}
          <div
            style={{
              display: "inline-block",
              background: "linear-gradient(to right, #f59e0b, #ef4444)",
              color: "white",
              padding: "4px 12px",
              borderRadius: "9999px",
              fontSize: "12px",
              fontWeight: "bold",
              marginBottom: "8px",
              boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
            }}
          >
            BETA - Name Subject to Change
          </div>

          <p
            style={{
              color: theme.text === "#ffffff" ? "#d1d5db" : "#6b7280",
              marginTop: "16px",
              fontSize: "18px",
            }}
          >
            Find words that connect before or after the pivot
          </p>

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "16px",
              marginTop: "16px",
              flexWrap: "wrap",
            }}
          >
            {settings.gameMode === "casual" ? (
              <div style={{ ...styles.infoBox, background: "#dcfce7", color: "#16a34a" }}>
                <Zap size={20} />
                <span style={{ fontWeight: "600" }}>Casual</span>
              </div>
            ) : settings.gameMode === "hard" ? (
              <div style={{ ...styles.infoBox, background: "#fee2e2", color: "#dc2626" }}>
                <Flame size={20} />
                <span style={{ fontFamily: "monospace", fontWeight: "600", fontSize: "18px" }}>
                  {formatTime(timeElapsed)}
                </span>
              </div>
            ) : (
              <div style={styles.infoBox}>
                <Clock size={20} />
                <span style={{ fontFamily: "monospace", fontWeight: "600", fontSize: "18px" }}>
                  {formatTime(timeElapsed)}
                </span>
              </div>
            )}
            <div style={styles.infoBox}>
              <Calendar size={20} />
              <span style={{ fontWeight: "600" }}>
                Puzzle #{gameData.puzzleNumber}
              </span>
            </div>
            <div
              style={{
                ...styles.infoBox,
                background: diffColor.bg,
                color: diffColor.text,
              }}
            >
              <Target size={20} />
              <span style={{ fontWeight: "bold", textTransform: "capitalize" }}>
                {gameData.difficulty}
              </span>
            </div>
          </div>

          {stats.currentStreak > 0 && (
            <div style={{ marginTop: "12px" }}>
              <div
                style={{
                  display: "inline-block",
                  background: "linear-gradient(to right, #fbbf24, #f59e0b)",
                  padding: "8px 20px",
                  borderRadius: "9999px",
                  boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                }}
              >
                <span
                  style={{
                    fontSize: "16px",
                    fontWeight: "bold",
                    color: "white",
                  }}
                >
                  <Flame size={20} fill="currentColor" /> {stats.currentStreak}{" "}
                  Day Streak!
                </span>
              </div>
            </div>
          )}
          {/* Wrong Guesses Indicator (Standard mode only) */}
          {settings.gameMode !== "casual" && isPlaying && (
            <div style={{ marginTop: "12px" }}>
              <div
                style={{
                  display: "inline-flex",
                  gap: "6px",
                  background: wrongGuesses >= 4 ? "#fee2e2" : "rgba(255,255,255,0.6)",
                  padding: "8px 16px",
                  borderRadius: "9999px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                }}
              >
                {[...Array(MAX_WRONG_GUESSES)].map((_, i) => (
                  <div
                    key={i}
                    style={{
                      width: "12px",
                      height: "12px",
                      borderRadius: "50%",
                      background: i < wrongGuesses ? "#ef4444" : "#e5e7eb",
                      transition: "background 0.3s",
                    }}
                    className={i === wrongGuesses - 1 ? "pulse-animation" : ""}
                  />
                ))}
                <span style={{ marginLeft: "8px", fontSize: "14px", color: wrongGuesses >= 4 ? "#dc2626" : "#6b7280", fontWeight: "600" }}>
                  {MAX_WRONG_GUESSES - wrongGuesses} left
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Game Container */}
        <div style={styles.gameContainer}>
          <div style={{ marginBottom: "24px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "8px",
              }}
            >
              <span
                style={{
                  fontWeight: "bold",
                  fontSize: "18px",
                  color: rankInfo.color,
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                {rankInfo.icon} {rankInfo.rank}
              </span>
              <span style={{ fontWeight: "600", color: theme.text, display: "flex", alignItems: "center", gap: "8px" }}>
                {baseWordsFound} / {TARGET_WORDS}
                {bonusWords > 0 && (
                  <span style={{
                    color: "#f59e0b",
                    fontWeight: "700",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                    background: "#fef3c7",
                    padding: "2px 8px",
                    borderRadius: "9999px",
                    fontSize: "14px",
                  }}>
                    <Star size={14} fill="currentColor" /> +{bonusWords}
                  </span>
                )}
              </span>
            </div>
            <div style={styles.progressBar}>
              <div style={styles.progressFill}></div>
            </div>
          </div>

          <div style={{ marginBottom: "24px" }}>
            <div className="input-container">
              <input
                type="text"
                value={beforeInput}
                onChange={(e) => setBeforeInput(e.target.value)}
                onKeyPress={async (e) => {
                  if (e.key === "Enter") {
                    await checkWord(beforeInput, "before");
                  }
                }}
                style={{
                  ...styles.input,
                  animation: shake ? "shake 0.5s" : "none",
                  borderColor: shake ? "#ef4444" : "#e5e7eb",
                }}
                placeholder="Word BEFORE..."
                disabled={!isPlaying || isGameOver}
                enterKeyHint="go"
                onFocus={() => setActiveInput("before")}
              />
              <button
                className="submit-btn"
                onClick={async () => await checkWord(beforeInput, "before")}
                disabled={!isPlaying || !beforeInput.trim() || isGameOver}
                style={{
                  background: !isPlaying || !beforeInput.trim() || isGameOver
                    ? "#d1d5db"
                    : "linear-gradient(135deg, #22c55e, #16a34a)",
                }}
              >
                <ArrowUp size={20} />
              </button>
            </div>
          </div>

          <div style={{ textAlign: "center", margin: "40px 0", padding: "0 10px" }}>
            <div
              id="pivot-word"
              className="pivot-word-responsive"
              style={{
                ...styles.pivotWord,
                maxWidth: "100%",
                boxSizing: "border-box",
              }}
            >
              {gameData.pivotWord}
            </div>
          </div>

          <div style={{ marginBottom: "24px" }}>
            <div className="input-container">
              <input
                type="text"
                value={afterInput}
                onChange={(e) => setAfterInput(e.target.value)}
                onKeyPress={async (e) => {
                  if (e.key === "Enter") {
                    await checkWord(afterInput, "after");
                  }
                }}
                style={{
                  ...styles.input,
                  animation: shake ? "shake 0.5s" : "none",
                  borderColor: shake ? "#ef4444" : "#e5e7eb",
                }}
                placeholder="Word AFTER..."
                disabled={!isPlaying || isGameOver}
                enterKeyHint="go"
                onFocus={() => setActiveInput("after")}
              />
              <button
                className="submit-btn"
                onClick={async () => await checkWord(afterInput, "after")}
                disabled={foundWords === totalWords || !isPlaying || !afterInput.trim() || isGameOver}
                style={{
                  background: !isPlaying || !afterInput.trim() || isGameOver
                    ? "#d1d5db"
                    : "linear-gradient(135deg, #9333ea, #7c3aed)",
                                  }}
              >
                <ArrowDown size={20} />
              </button>
            </div>
          </div>

          {message && (
            <div
              style={{
                textAlign: "center",
                padding: "12px 24px",
                borderRadius: "16px",
                marginBottom: "24px",
                fontWeight: "600",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                background:
                  message.type === "success"
                    ? "linear-gradient(to right, #4ade80, #10b981)"
                    : message.type === "bonus"
                      ? "linear-gradient(to right, #fbbf24, #d97706)"
                      : "linear-gradient(to right, #fb923c, #ef4444)",
                color: "white",
                boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
              }}
            >
              {message.text}
            </div>
          )}
          {/* Hint Button */}
          {settings.gameMode !== "hard" && foundWords < totalWords && !isGameOver && (
            <div style={{ marginBottom: "24px", textAlign: "center" }}>
              <button
                onClick={showNextHint}
                disabled={currentHintIndex >= gameData.hints.length - 1}
                style={{
                  padding: "12px 24px",
                  background:
                    currentHintIndex >= gameData.hints.length - 1
                      ? "#d1d5db"
                      : theme.pivotBg,
                  color: theme.text === "#ffffff" ? "#ffffff" : "white",
                  fontWeight: "600",
                  borderRadius: "9999px",
                  border: "none",
                  cursor:
                    currentHintIndex >= gameData.hints.length - 1
                      ? "not-allowed"
                      : "pointer",
                  boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)",
                  transition: "all 0.2s",
                  fontSize: "16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                }}
              >
                <Lightbulb size={20} /> {currentHintIndex === -1 ? "Get a Hint" : "Next Hint"} (
                {Math.min(currentHintIndex + 1, gameData.hints.length)}/
                {gameData.hints.length})
              </button>
              {currentHintIndex >= 0 && (
                <div
                  style={{
                    marginTop: "16px",
                    padding: "16px",
                    background: "#fef3c7",
                    border: "2px solid #fbbf24",
                    borderRadius: "16px",
                    animation: "scaleIn 0.3s ease-out",
                  }}
                >
                  <p style={{ color: "#92400e", fontWeight: "500", margin: 0 }}>
                    {gameData.hints[currentHintIndex]}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Suggest a Word Button */}
          <div style={{ textAlign: "center", marginBottom: "16px" }}>
            <button
              onClick={() => setShowSuggestModal(true)}
              style={{
                background: "none",
                border: "none",
                color: "#6366f1",
                cursor: "pointer",
                fontSize: "14px",
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                padding: "8px 16px",
              }}
            >
              <MessageSquare size={16} /> Think we missed a word?
            </button>
          </div>

          <div
            className="grid-container"
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "24px",
              marginTop: "32px",
            }}
          >
            <div
              style={{
                background: themes.light.cardBefore,
                borderRadius: "16px",
                padding: "24px",
                border: "1px solid #bbf7d0",
              }}
            >
              <h3
                style={{
                  fontWeight: "bold",
                  color: "#166534",
                  marginBottom: "16px",
                  fontSize: "18px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <ArrowUp size={24} />
                {settings.gameMode === "hard" ? "" : `(${foundBefore.length}/${gameData.validBefore.length})`}
              </h3>
              <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                {foundBefore.length === 0 ? (
                  <p
                    style={{
                      color: "#86efac",
                      fontStyle: "italic",
                      fontSize: "14px",
                    }}
                  >
                    No words found yet...
                  </p>
                ) : settings.gameMode === "hard" ? (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "20px",
                      fontSize: "32px",
                      fontWeight: "900",
                      color: "#166534",
                    }}
                  >
                    {foundBefore.length} / {gameData.validBefore.length}
                  </div>
                ) : (
                  foundBefore.map((word, idx) => (
                    <div
                      key={idx}
                      style={{ ...styles.foundWordCard, color: "#166534" }}
                    >
                      <Check size={16} color="#4ade80" />
                      {word} <span style={{ color: "#9ca3af", margin: "0 4px" }}>+</span> {gameData.pivotWord.toLowerCase()}
                    </div>
                  ))
                )}
              </div>
            </div>
            <div
              style={{
                background: themes.light.cardAfter,
                borderRadius: "16px",
                padding: "24px",
                border: "1px solid #e9d5ff",
              }}
            >
              <h3
                style={{
                  fontWeight: "bold",
                  color: "#7e22ce",
                  marginBottom: "16px",
                  fontSize: "18px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <ArrowDown size={24} />
                {settings.gameMode === "hard" ? "" : `(${foundAfter.length}/${gameData.validAfter.length})`}
              </h3>
              <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                {foundAfter.length === 0 ? (
                  <p
                    style={{
                      color: "#d8b4fe",
                      fontStyle: "italic",
                      fontSize: "14px",
                    }}
                  >
                    No words found yet...
                  </p>
                ) : settings.gameMode === "hard" ? (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "20px",
                      fontSize: "32px",
                      fontWeight: "900",
                      color: "#7e22ce",
                    }}
                  >
                    {foundAfter.length} / {gameData.validAfter.length}
                  </div>
                ) : (
                  foundAfter.map((word, idx) => (
                    <div
                      key={idx}
                      style={{ ...styles.foundWordCard, color: "#7e22ce" }}
                    >
                      <Check size={16} color="#a855f7" />
                      {gameData.pivotWord.toLowerCase()} <span style={{ color: "#9ca3af", margin: "0 4px" }}>+</span> {word}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          <div style={{ marginTop: "24px", textAlign: "center" }}>
            <div
              style={{
                display: "inline-block",
                background: "rgba(255,255,255,0.6)",
                backdropFilter: "blur(16px)",
                borderRadius: "16px",
                padding: "12px 24px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              }}
            >
              <p
                style={{
                  color: theme.text === "#ffffff" ? "#d1d5db" : "#6b7280",
                  fontSize: "14px",
                  margin: 0,
                }}
              >
                Tap the arrow to submit •{" "}
                <button
                  onClick={() => setShowTutorial(true)}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#6366f1",
                    cursor: "pointer",
                    textDecoration: "underline",
                    fontWeight: "500",
                    padding: 0,
                  }}
                >
                  How to Play
                </button>
              </p>
            </div>

            {/* Onscreen Keyboard */}
            {showKeyboard && isPlaying && !isGameOver && foundWords < totalWords && (
              <div
                style={{
                  marginTop: "24px",
                  padding: "16px",
                  background: theme.gameBg,
                  borderRadius: "16px",
                  border: `1px solid ${theme.text}20`,
                }}
              >
                {/* Input Selection Tabs */}
                <div style={{ display: "flex", gap: "8px", marginBottom: "16px" }}>
                  <button
                    onClick={() => setActiveInput("before")}
                    style={{
                      flex: 1,
                      padding: "12px",
                      borderRadius: "12px",
                      border: "2px solid",
                      borderColor: activeInput === "before" ? "#22c55e" : "#e5e7eb",
                      background: activeInput === "before" ? "#dcfce7" : "white",
                      color: activeInput === "before" ? "#16a34a" : "#6b7280",
                      fontWeight: "600",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px",
                    }}
                  >
                    <ArrowUp size={18} /> Before
                  </button>
                  <button
                    onClick={() => setActiveInput("after")}
                    style={{
                      flex: 1,
                      padding: "12px",
                      borderRadius: "12px",
                      border: "2px solid",
                      borderColor: activeInput === "after" ? "#9333ea" : "#e5e7eb",
                      background: activeInput === "after" ? "#f3e8ff" : "white",
                      color: activeInput === "after" ? "#9333ea" : "#6b7280",
                      fontWeight: "600",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "8px",
                    }}
                  >
                    <ArrowDown size={18} /> After
                  </button>
                </div>

                {/* Current Input Display */}
                <div
                  style={{
                    padding: "16px",
                    background: activeInput === "before" ? "#f0fdf4" : "#faf5ff",
                    borderRadius: "12px",
                    marginBottom: "16px",
                    textAlign: "center",
                    minHeight: "56px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: `2px solid ${activeInput === "before" ? "#22c55e" : "#9333ea"}`,
                  }}
                >
                  <span
                    style={{
                      fontSize: "24px",
                      fontWeight: "bold",
                      color: activeInput === "before" ? "#16a34a" : "#9333ea",
                      textTransform: "lowercase",
                    }}
                  >
                    {activeInput === "before" ? (
                      <>
                        {beforeInput || <span style={{ opacity: 0.4 }}>type here</span>}
                        <span style={{ color: "#9ca3af", margin: "0 8px" }}>+</span>
                        <span style={{ opacity: 0.6 }}>{gameData.pivotWord.toLowerCase()}</span>
                      </>
                    ) : (
                      <>
                        <span style={{ opacity: 0.6 }}>{gameData.pivotWord.toLowerCase()}</span>
                        <span style={{ color: "#9ca3af", margin: "0 8px" }}>+</span>
                        {afterInput || <span style={{ opacity: 0.4 }}>type here</span>}
                      </>
                    )}
                  </span>
                </div>

                {/* Keyboard Rows */}
                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                  {keyboardRows.map((row, rowIndex) => (
                    <div
                      key={rowIndex}
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        gap: "4px",
                      }}
                    >
                      {row.map((key) => (
                        <KeyboardButton
                          key={key}
                          keyVal={key}
                          wide={key === "ENTER" || key === "DEL"}
                        />
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Copyright and Terms Footer */}
            <div
              style={{
                marginTop: "24px",
                paddingTop: "24px",
                borderTop: "1px solid rgba(255,255,255,0.2)",
                textAlign: "center",
                color: theme.text === "#ffffff" ? "#cbd5e1" : "#4b5563",
                fontSize: "12px",
              }}
            >
              © {new Date().getFullYear()} Pivot Game. Jorge Contreras Mendez. All rights reserved.
              <div style={{ marginTop: "8px" }}>
                <button
                  onClick={() => setShowTerms(true)}
                  style={{
                    background: "none",
                    border: "none",
                    color: "inherit",
                    textDecoration: "underline",
                    cursor: "pointer",
                    fontSize: "11px",
                    padding: "4px",
                    opacity: 0.8,
                  }}
                >
                  Terms of Service
                </button>
                <span style={{ margin: "0 4px", opacity: 0.5 }}>•</span>
                <button
                  onClick={() => setShowCopyright(true)}
                  style={{
                    background: "none",
                    border: "none",
                    color: "inherit",
                    textDecoration: "underline",
                    cursor: "pointer",
                    fontSize: "11px",
                    padding: "4px",
                    opacity: 0.8,
                  }}
                >
                  Copyright Notice
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div >
  );
};

export default PivotGame;
